/**
 * hsp-headset-test.js
 *
 * The in-browser headset test at /pages/headset-test, and the compact version the
 * [headset-test] blog shortcode embeds. Markup shell: snippets/hsp-headset-test.liquid.
 *
 * WHAT THIS MEASURES THAT OTHER MIC TESTS DO NOT
 *
 * 1. Voice bandwidth. A codec leaves a brick-wall cliff in the spectrum at half its
 *    sample rate: ~4 kHz for Bluetooth hands-free (CVSD, "phone quality"), ~8 kHz
 *    for wideband (mSBC and most USB office headsets), higher for full-band mics.
 *    Natural speech rolls off gradually, so a cliff of 18 dB inside ~1 kHz that
 *    stays down is the codec, not the voice. This is what explains "I sound
 *    muffled on Bluetooth" - the headset dropped into hands-free mode.
 * 2. Echo and leak. We play a log sweep through the output and cross-correlate the
 *    mic capture against it. The correlation peak gives round-trip latency and
 *    how loudly the mic hears the earpiece, which is the "they hear an echo of
 *    themselves" complaint.
 * 3. Which device this actually is. Chromium labels carry the USB vendor:product
 *    id, so the brand is known even when the name is generic.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *
 * - Upload anything. Audio lives in memory and dies with the tab. The only network
 *   call is /search/suggest.json with the device NAME, to link the matching product.
 * - Measure with browser processing on. Echo cancellation, noise suppression and
 *   auto gain are requested OFF, so the numbers describe the hardware. Teams and
 *   Zoom add their own processing on top.
 * - Use MediaRecorder. Playback and download are built from the captured PCM as
 *   WAV, which every browser plays, instead of juggling webm/mp4 support.
 *
 * The DSP and parsing helpers are exported pure functions so
 * headset-plus-seo/scripts/selftest-headset-test.js can test them in Node; the
 * custom element is only defined when a DOM exists.
 */

/* ------------------------------------------------------------------ */
/* DSP - pure, no DOM                                                  */
/* ------------------------------------------------------------------ */

export function rms(buf, start = 0, end = buf.length) {
  let s = 0;
  const n = Math.max(0, end - start);
  if (!n) return 0;
  for (let i = start; i < end; i++) s += buf[i] * buf[i];
  return Math.sqrt(s / n);
}

export function toDb(x) {
  return x > 0 ? 20 * Math.log10(x) : -Infinity;
}

export function nextPow2(n) {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

/** In-place iterative radix-2 complex FFT. re/im lengths must be a power of two. */
export function fft(re, im, inverse = false) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      let t = re[i]; re[i] = re[j]; re[j] = t;
      t = im[i]; im[i] = im[j]; im[j] = t;
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = ((inverse ? 2 : -2) * Math.PI) / len;
    const wr = Math.cos(ang);
    const wi = Math.sin(ang);
    const half = len >> 1;
    for (let i = 0; i < n; i += len) {
      let cr = 1;
      let ci = 0;
      for (let k = 0; k < half; k++) {
        const a = i + k;
        const b = a + half;
        const xr = re[b] * cr - im[b] * ci;
        const xi = re[b] * ci + im[b] * cr;
        re[b] = re[a] - xr; im[b] = im[a] - xi;
        re[a] += xr; im[a] += xi;
        const t = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = t;
      }
    }
  }
  if (inverse) {
    for (let i = 0; i < n; i++) { re[i] /= n; im[i] /= n; }
  }
}

/** RMS in dBFS of consecutive frames. */
export function frameDb(buf, sr, frameMs = 20) {
  const n = Math.max(1, Math.round((sr * frameMs) / 1000));
  const out = [];
  for (let i = 0; i + n <= buf.length; i += n) out.push(toDb(rms(buf, i, i + n)));
  return out;
}

function percentile(arr, p) {
  const a = arr.filter(Number.isFinite).sort((x, y) => x - y);
  if (!a.length) return -Infinity;
  return a[Math.min(a.length - 1, Math.max(0, Math.round((p / 100) * (a.length - 1))))];
}

/**
 * Level statistics for a speech capture.
 * speechDb is the RMS of the frames within 20 dB of the loudest ones (the talking,
 * not the pauses); peakDb is the 99.9th percentile sample so one click does not
 * count as the peak; noiseDb estimates the floor from the quietest frames.
 */
export function speechStats(buf, sr) {
  const frames = frameDb(buf, sr, 20);
  const loud = percentile(frames, 95);
  const active = frames.filter((d) => d > loud - 20);
  const lin = active.map((d) => Math.pow(10, d / 10));
  const speechDb = lin.length ? 10 * Math.log10(lin.reduce((a, b) => a + b, 0) / lin.length) : -Infinity;
  const abs = new Float32Array(buf.length);
  let clipped = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = Math.abs(buf[i]);
    abs[i] = v;
    if (v >= 0.99) clipped++;
  }
  const sorted = abs.sort();
  const peak = sorted.length ? sorted[Math.floor(0.999 * (sorted.length - 1))] : 0;
  return {
    speechDb,
    peakDb: toDb(peak),
    clipRatio: buf.length ? clipped / buf.length : 0,
    noiseDb: percentile(frames, 10),
    activeRatio: frames.length ? active.length / frames.length : 0,
  };
}

/**
 * Long-term power spectrum of the loud frames, as dB per `bandHz` band.
 * Returns { bands: Float64Array (dB), bandHz }.
 */
export function bandSpectrum(buf, sr, { n = 4096, bandHz = 125 } = {}) {
  const hop = n >> 1;
  const bins = n >> 1;
  const power = new Float64Array(bins);
  const win = new Float64Array(n);
  for (let i = 0; i < n; i++) win[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1));
  const starts = [];
  const levels = [];
  for (let s = 0; s + n <= buf.length; s += hop) {
    starts.push(s);
    levels.push(toDb(rms(buf, s, s + n)));
  }
  const loud = percentile(levels, 90);
  const re = new Float64Array(n);
  const im = new Float64Array(n);
  let used = 0;
  for (let f = 0; f < starts.length; f++) {
    if (!(levels[f] > loud - 25)) continue;
    const s = starts[f];
    for (let i = 0; i < n; i++) { re[i] = buf[s + i] * win[i]; im[i] = 0; }
    fft(re, im);
    for (let k = 0; k < bins; k++) power[k] += re[k] * re[k] + im[k] * im[k];
    used++;
  }
  const binHz = sr / n;
  const nb = Math.floor(sr / 2 / bandHz);
  const bands = new Float64Array(nb).fill(-Infinity);
  if (!used) return { bands, bandHz };
  for (let b = 0; b < nb; b++) {
    const k0 = Math.floor((b * bandHz) / binHz);
    const k1 = Math.min(bins, Math.floor(((b + 1) * bandHz) / binHz));
    let s = 0;
    for (let k = k0; k < k1; k++) s += power[k];
    const m = k1 > k0 ? s / (k1 - k0) / used : 0;
    // 1e-30 floor: a digitally silent band is "very far down", not NaN.
    bands[b] = 10 * Math.log10(m + 1e-30);
  }
  return { bands, bandHz };
}

function meanBand(bands, bandHz, f0, f1) {
  const b0 = Math.max(0, Math.floor(f0 / bandHz));
  const b1 = Math.min(bands.length, Math.ceil(f1 / bandHz));
  let s = 0;
  let c = 0;
  for (let b = b0; b < b1; b++) { s += bands[b]; c++; }
  return c ? s / c : -Infinity;
}

function maxBand(bands, bandHz, f0, f1) {
  const b0 = Math.max(0, Math.floor(f0 / bandHz));
  const b1 = Math.min(bands.length, Math.ceil(f1 / bandHz));
  let m = -Infinity;
  for (let b = b0; b < b1; b++) m = Math.max(m, bands[b]);
  return m;
}

export const BANDWIDTH = {
  narrow: { max: 5000, label: 'Narrowband', note: 'phone quality' },
  wide: { max: 9000, label: 'Wideband', note: 'HD voice' },
  super: { max: 17000, label: 'Super-wideband', note: 'better than HD voice' },
  full: { max: Infinity, label: 'Full band', note: 'studio range' },
};

/**
 * Finds the codec cliff. Scans upward from 3 kHz for the first edge where the
 * kilohertz below is >= 18 dB louder than the kilohertz above AND nothing above
 * climbs back within 12 dB. No cliff below the top of the range = full band.
 */
export function analyseBandwidth(buf, sr) {
  const { bands, bandHz } = bandSpectrum(buf, sr);
  const top = Math.min(sr / 2 - 500, 20000);
  const ref = meanBand(bands, bandHz, 300, 1000);
  if (!Number.isFinite(ref)) return { cutoffHz: null, cls: 'unknown' };
  let cutoff = null;
  for (let e = 3000; e <= top - 1200; e += bandHz) {
    const below = meanBand(bands, bandHz, e - 1000, e - 150);
    const above = meanBand(bands, bandHz, e + 150, e + 1000);
    const rest = maxBand(bands, bandHz, e + 150, top);
    if (below - above >= 18 && below - rest >= 12) { cutoff = e; break; }
  }
  const cutoffHz = cutoff === null ? Math.round(top) : cutoff;
  let cls = 'full';
  if (cutoffHz <= BANDWIDTH.narrow.max) cls = 'narrow';
  else if (cutoffHz <= BANDWIDTH.wide.max) cls = 'wide';
  else if (cutoffHz <= BANDWIDTH.super.max) cls = 'super';
  return { cutoffHz, cls };
}

/**
 * Cross-correlates `sig` against `ref` via FFT and returns the strongest lag.
 * gain is the path gain at that lag (1 = the mic heard the reference at full
 * level); psr is peak-to-RMS of the correlation, the confidence that the peak is
 * a real echo path and not noise (a matched sweep in noise sits well above 10).
 */
export function xcorr(ref, sig) {
  const n = nextPow2(ref.length + sig.length);
  const ar = new Float64Array(n);
  const ai = new Float64Array(n);
  const br = new Float64Array(n);
  const bi = new Float64Array(n);
  for (let i = 0; i < sig.length; i++) ar[i] = sig[i];
  for (let i = 0; i < ref.length; i++) br[i] = ref[i];
  fft(ar, ai);
  fft(br, bi);
  for (let k = 0; k < n; k++) {
    const r = ar[k] * br[k] + ai[k] * bi[k];
    const im = ai[k] * br[k] - ar[k] * bi[k];
    ar[k] = r; ai[k] = im;
  }
  fft(ar, ai, true);
  const maxLag = Math.max(1, sig.length - Math.floor(ref.length / 2));
  let best = 0;
  let bestV = 0;
  let sq = 0;
  for (let k = 0; k < maxLag; k++) {
    const v = Math.abs(ar[k]);
    sq += v * v;
    if (v > bestV) { bestV = v; best = k; }
  }
  let energy = 0;
  for (let i = 0; i < ref.length; i++) energy += ref[i] * ref[i];
  const corrRms = Math.sqrt(sq / maxLag) || 1e-30;
  return { lag: best, gain: energy ? bestV / energy : 0, psr: bestV / corrRms };
}

/** Exponential sine sweep with 20 ms fades. */
export function makeLogSweep(sr, dur, f0, f1, amp) {
  const n = Math.round(sr * dur);
  const out = new Float32Array(n);
  const k = Math.log(f1 / f0);
  const fade = Math.round(sr * 0.02);
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const ph = ((2 * Math.PI * f0 * dur) / k) * (Math.exp((t / dur) * k) - 1);
    let g = amp;
    if (i < fade) g *= i / fade;
    else if (i > n - fade) g *= (n - i) / fade;
    out[i] = g * Math.sin(ph);
  }
  return out;
}

/** Seeded pink noise (Paul Kellet's filter), normalised to `amp` peak. */
export function pinkNoise(n, amp = 0.25, seed = 1) {
  let s = seed >>> 0 || 1;
  const rnd = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296 * 2 - 1;
  };
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  const out = new Float32Array(n);
  let peak = 0;
  for (let i = 0; i < n; i++) {
    const w = rnd();
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    const v = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362;
    b6 = w * 0.115926;
    out[i] = v;
    peak = Math.max(peak, Math.abs(v));
  }
  const g = peak ? amp / peak : 0;
  const fade = Math.min(n >> 2, 960);
  for (let i = 0; i < n; i++) {
    let f = 1;
    if (i < fade) f = i / fade;
    else if (i > n - fade) f = (n - i) / fade;
    out[i] *= g * f;
  }
  return out;
}

/** 16-bit mono WAV. */
export function encodeWav(samples, sr) {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buf);
  const str = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); v.setUint32(4, 36 + samples.length * 2, true); str(8, 'WAVE');
  str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  str(36, 'data'); v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const x = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(44 + i * 2, x < 0 ? x * 0x8000 : x * 0x7fff, true);
  }
  return buf;
}

/* ------------------------------------------------------------------ */
/* Device labels                                                       */
/* ------------------------------------------------------------------ */

const VENDOR_IDS = {
  '0b0e': 'Jabra',
  '047f': 'Poly',
  '03f0': 'Poly',
  '6993': 'Yealink',
  '046d': 'Logitech',
  '1395': 'EPOS',
  '045e': 'Microsoft',
  '05ac': 'Apple',
};

const BRAND_WORDS = [
  [/\b(jabra|gn netcom)\b/i, 'Jabra'],
  [/\b(poly|plantronics|polycom)\b/i, 'Poly'],
  [/\byealink\b/i, 'Yealink'],
  [/\blogi(tech)?\b/i, 'Logitech'],
  [/\bepos\b/i, 'EPOS'],
  [/\bsennheiser\b/i, 'Sennheiser'],
  [/\bcisco\b/i, 'Cisco'],
  [/\bmicrosoft\b/i, 'Microsoft'],
  [/\b(airpods|beats)\b/i, 'Apple'],
  [/\bsony\b|\bwh-1000|\bwf-1000/i, 'Sony'],
  [/\bbose\b/i, 'Bose'],
  [/\b(samsung|galaxy buds)\b/i, 'Samsung'],
  [/\b(anker|soundcore)\b/i, 'Anker'],
];

const GENERIC_OUTER = /^(headset microphone|headset|microphone|mic|headphones|headset earphone|earphones|speakers?|line|input|communications headset)$/i;
const BUILTIN = /microphone array|built-?in|internal mic|macbook|imac|mac mini|intel.*smart sound|smart sound|conexant|synaptics|high definition audio|surface/i;
const REALTEK = /realtek/i;
const BT = /hands-?free|ag audio|\bbluetooth\b/i;
const DONGLE = /\blink ?\d{3}\b|\bbt ?\d{3}\b|\bdongle\b|\busb adapter\b/i;

/**
 * Turns a browser device label into { name, brand, vid, pid, connection, dongle }.
 * connection: 'usb' | 'bluetooth' | 'builtin' | 'analog' | 'unknown'.
 * Examples (Chromium on Windows):
 *   "Default - Headset Microphone (Jabra Evolve2 65) (0b0e:030c)"
 *   "Headset (WH-1000XM4 Hands-Free AG Audio)"
 *   "Microphone Array (Realtek(R) Audio)"
 *   "Headset Microphone (Realtek(R) Audio)"   <- 3.5 mm combo jack
 * macOS and Firefox give bare names ("Jabra Evolve2 65", "MacBook Pro Microphone").
 */
export function parseDeviceLabel(label) {
  let s = String(label || '').trim();
  const out = { raw: s, name: '', brand: null, vid: null, pid: null, connection: 'unknown', dongle: false };
  if (!s) return out;
  const idm = s.match(/\(([0-9a-f]{4}):([0-9a-f]{4})\)\s*$/i);
  if (idm) {
    out.vid = idm[1].toLowerCase();
    out.pid = idm[2].toLowerCase();
    s = s.slice(0, idm.index).trim();
  }
  s = s.replace(/^(default|communications)\s*-\s*/i, '').replace(/\(bluetooth\)\s*$/i, '').trim();
  const isBt = BT.test(s);
  const isRealtek = REALTEK.test(s);
  const isBuiltin = BUILTIN.test(s);
  // "Outer (Inner)": keep the inner name when the outer is a generic role word.
  const pm = s.match(/^(.*?)\s*\((.+)\)\s*$/);
  let name = s;
  if (pm && GENERIC_OUTER.test(pm[1].trim())) name = pm[2];
  name = name
    .replace(/hands-?free( ag audio)?/i, '')
    .replace(/\bag audio\b/i, '')
    .replace(/\((r|tm)\)|®|™/gi, '')
    .replace(/\s+series\b/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  out.name = name || s;
  out.brand = (out.vid && VENDOR_IDS[out.vid]) || null;
  if (!out.brand) {
    for (const [re, b] of BRAND_WORDS) if (re.test(s)) { out.brand = b; break; }
  }
  out.dongle = DONGLE.test(s);
  if (isBt) out.connection = 'bluetooth';
  else if (isRealtek && /headset|headphone|line|mic in|front|rear/i.test(s) && !/array/i.test(s)) out.connection = 'analog';
  else if (isBuiltin || isRealtek) out.connection = 'builtin';
  else if (/external microphone|line in/i.test(s)) out.connection = 'analog';
  else if (out.vid) out.connection = 'usb';
  return out;
}

const NOISE_TOKENS = new Set([
  'series', 'usb', 'uc', 'ms', 'teams', 'headset', 'headsets', 'microphone', 'mic', 'stereo', 'mono',
  'wireless', 'audio', 'hands', 'free', 'ag', 'the', 'for', 'with', 'and', 'duo', 'bluetooth', 'plus',
]);

/** Tokens a product title must contain to count as this model. Needs >= 1 with a digit. */
export function modelTokens(name) {
  const words = String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ');
  const brandish = /^(jabra|gn|poly|plantronics|polycom|yealink|logitech|logi|epos|sennheiser|hp|cisco|microsoft)$/;
  const toks = words.filter((w) => w && !NOISE_TOKENS.has(w) && !brandish.test(w) && (/\d/.test(w) || w.length >= 3));
  return toks.some((t) => /\d/.test(t)) ? toks : [];
}

const ACCESSORY = /\b(stand|cushions?|ear ?pads?|cable|cord|case|charger|charging|battery|replacement|spare|clip|mount|pouch)\b/i;

/** True when `title` names the same model and is not an accessory for it. */
export function titleMatches(title, tokens) {
  if (!tokens || !tokens.length) return false;
  const norm = ' ' + String(title || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ') + ' ';
  if (!tokens.every((t) => norm.includes(' ' + t + ' '))) return false;
  if (ACCESSORY.test(title) && !tokens.some((t) => ACCESSORY.test(t))) return false;
  return true;
}

/* ------------------------------------------------------------------ */
/* Which device is the computer's default                              */
/* ------------------------------------------------------------------ */

// Webcam before brand: Logitech makes both, and "Logitech C920" must not read as a headset.
const WEBCAM = /webcam|web cam|\bcamera\b|\bcam\b|\bc(270|310|505|615|9\d\d)[a-z]?\b|\bbrio\b|streamcam|kiyo|facecam|lifecam|\bhd pro\b/i;
const HEADSET_WORDS = /headset|headphone|earphone|ear ?buds?|\bbuds\b|hands-?free|airpods|evolve|blackwire|voyager|savi|engage|encorepro|\bbiz ?\d|\bzone\b|wh-1000|\bbh\d{2}|\buh\d{2}/i;
const HEADSET_BRANDS = new Set(['Jabra', 'Poly', 'Yealink', 'EPOS', 'Sennheiser']);

/** 'webcam' | 'builtin' | 'headset' | 'other' for a label or a parseDeviceLabel() result. */
export function deviceKind(label) {
  const d = typeof label === 'string' ? parseDeviceLabel(label) : label;
  if (!d) return 'other';
  const raw = d.raw || d.name || '';
  if (WEBCAM.test(raw)) return 'webcam';
  if (d.connection === 'builtin') return 'builtin';
  if (HEADSET_WORDS.test(raw) || HEADSET_BRANDS.has(d.brand) || d.connection === 'bluetooth') return 'headset';
  return 'other';
}

/**
 * What the computer uses by default, from enumerateDevices(). Chromium lists the OS
 * default as deviceId "default" (and, on Windows, the default communications device
 * as "communications"); Firefox and Safari list neither, so there def is null and
 * nothing is flagged, rather than guessing.
 * Returns { mic, spk }, each { def, defKind, comm, headset: { id, p } | undefined }.
 */
export function defaultCheck(inputs, outputs) {
  const pick = (list) => {
    const byId = (id) => list.find((d) => d.deviceId === id);
    const def = byId('default') ? parseDeviceLabel(byId('default').label) : null;
    const comm = byId('communications') ? parseDeviceLabel(byId('communications').label) : null;
    const headset = list
      .filter((d) => d.deviceId !== 'default' && d.deviceId !== 'communications' && d.label)
      .map((d) => ({ id: d.deviceId, p: parseDeviceLabel(d.label) }))
      .find((x) => deviceKind(x.p) === 'headset');
    return { def, defKind: def ? deviceKind(def) : null, comm, commKind: comm ? deviceKind(comm) : null, headset };
  };
  return { mic: pick(inputs || []), spk: pick(outputs || []) };
}

/** Which defaults are wrong: a headset is plugged in but something else is the default. */
export function defaultProblems(dc) {
  const mic = !!(dc.mic.def && dc.mic.headset && dc.mic.defKind !== 'headset');
  const spk = !!(dc.spk.def && dc.spk.headset && dc.spk.defKind !== 'headset');
  const comm = !mic && !!(dc.mic.comm && dc.mic.headset && dc.mic.commKind !== 'headset');
  return { mic, spk, comm, any: mic || spk || comm };
}

/* ------------------------------------------------------------------ */
/* Scoring                                                             */
/* ------------------------------------------------------------------ */

export const T = {
  speechMissing: -50,
  quietPeak: -30,
  hotPeak: -1,
  clip: 0.001,
  noiseGood: -60,
  noiseBad: -45,
  snrGood: 25,
  snrBad: 15,
  leakWarn: -45,
  leakFail: -30,
  psr: 10,
};

/** Each returns { status: 'pass'|'warn'|'fail'|'skip', value, fix } - value/fix are plain text. */
export function scoreLevel(st) {
  if (!st) return { status: 'skip' };
  const value = `speech ${fmtDb(st.speechDb)} RMS, peak ${fmtDb(st.peakDb)}`;
  if (!(st.speechDb > T.speechMissing)) return { status: 'fail', value, fix: 'We could not hear you. Check the mute switch on the cord or earcup, then make sure this is the mic you are wearing.' };
  if (st.clipRatio >= T.clip || st.peakDb > T.hotPeak) return { status: 'warn', value: value + ', clipping', fix: 'Your voice is distorting. Move the boom a finger-width further from your mouth, or lower the mic level in your sound settings.' };
  if (st.peakDb < T.quietPeak) return { status: 'warn', value, fix: 'You are quiet. Bring the boom to the corner of your mouth, about two fingers away, and raise the mic level in your sound settings.' };
  return { status: 'pass', value };
}

export function scoreNoise(noiseDb, speechDb) {
  if (!Number.isFinite(noiseDb)) return { status: 'skip' };
  const snr = Number.isFinite(speechDb) ? speechDb - noiseDb : null;
  const value = `floor ${fmtDb(noiseDb)}` + (snr !== null ? `, voice ${Math.round(snr)} dB above it` : '');
  if (noiseDb > T.noiseBad || (snr !== null && snr < T.snrBad)) return { status: 'fail', value, fix: 'The mic hears a lot of the room. A noise-canceling boom mic rejects it at the source; software noise suppression only hides some of it.' };
  if (noiseDb > T.noiseGood || (snr !== null && snr < T.snrGood)) return { status: 'warn', value, fix: 'Some background noise gets through. Turn on noise suppression in your calling app, or point the boom away from fans and keyboards.' };
  return { status: 'pass', value };
}

export function scoreBandwidth(bw, dev) {
  if (!bw || bw.cls === 'unknown') return { status: 'skip' };
  const info = BANDWIDTH[bw.cls];
  const value = `${info.label}, about ${(bw.cutoffHz / 1000).toFixed(1)} kHz (${info.note})`;
  if (bw.cls === 'narrow') {
    const bt = dev && dev.connection === 'bluetooth';
    return {
      status: 'warn',
      value,
      fix: bt || !dev || dev.connection === 'unknown'
        ? 'This is phone-quality audio, which is what a Bluetooth headset sends when it connects to a computer directly: opening the mic switches it to hands-free mode. A USB dongle made for the headset keeps it in wideband.'
        : 'This is phone-quality audio. Check that your calling app or adapter is not forcing narrowband, or try the headset on a USB port directly.',
    };
  }
  return { status: 'pass', value };
}

export function scoreEcho(echo) {
  if (!echo) return { status: 'skip' };
  if (echo.heard === 'speakers') return { status: 'fail', value: 'sound is playing from the computer speakers', fix: 'Your calls are going to the wrong output. Pick the headset as the speaker in your system sound settings and in your calling app.' };
  if (echo.heard === 'none') return { status: 'fail', value: 'no sound heard', fix: 'Nothing reached your ears. Check the volume, the mute button, and which output is selected.' };
  const detected = echo.psr >= T.psr;
  const leak = detected ? toDb(echo.gain) : null;
  const value = detected ? `mic hears the earpiece ${Math.round(-leak)} dB below the played tone, round trip ${Math.round(echo.latencyMs)} ms` : 'mic does not hear the earpiece';
  if (detected && leak > T.leakFail) return { status: 'fail', value, fix: 'Your mic clearly hears your own speakers, which is what people on the call hear as an echo of themselves. Turn the headset volume down or use a closed-back headset.' };
  if (detected && leak > T.leakWarn) return { status: 'warn', value, fix: 'A little of the earpiece reaches the mic. Calling apps usually cancel this, but lower the volume if people report an echo.' };
  return { status: 'pass', value };
}

export function scoreSpeakers(sp) {
  if (!sp || !sp.left || !sp.right) return { status: 'skip' };
  const ok = sp.left === 'left' && sp.right === 'right';
  const swapped = sp.left === 'right' && sp.right === 'left';
  const value = `left channel heard: ${sp.left}, right channel heard: ${sp.right}`;
  if (ok) return { status: 'pass', value };
  if (swapped) return { status: 'warn', value, fix: 'Left and right are swapped. Turn the headset around, or check the balance setting in your sound settings.' };
  return { status: 'fail', value, fix: 'One side is missing. Check the plug is fully in, then the balance setting. A mono headset only has one side, and that is expected.' };
}

export function scoreDevice(dev, defaultLabel) {
  if (!dev) return { status: 'skip' };
  const value = dev.name + (dev.brand ? ` (${dev.brand})` : '');
  if (deviceKind(dev) === 'webcam') return { status: 'warn', value, fix: "This is your webcam's mic, not a headset. Choose your headset in the Microphone list, and make it your computer's default mic." };
  if (dev.connection === 'builtin') return { status: 'warn', value, fix: "This is your computer's built-in mic. If you are wearing a headset, choose it from the microphone list above." };
  if (defaultLabel && parseDeviceLabel(defaultLabel).name !== dev.name) {
    return { status: 'warn', value, fix: `Your system default mic is "${parseDeviceLabel(defaultLabel).name}". Apps set to "Default" will use that one, not this headset.` };
  }
  return { status: 'pass', value };
}

export function verdict(rows) {
  const st = rows.map((r) => r.status);
  if (st.includes('fail')) return 'fail';
  if (st.includes('warn')) return 'warn';
  return 'pass';
}

export function fmtDb(d) {
  return Number.isFinite(d) ? `${Math.round(d)} dBFS` : 'n/a';
}

const CONNECTION_TEXT = {
  usb: 'USB',
  bluetooth: 'Bluetooth (hands-free mode)',
  builtin: 'Built-in',
  analog: '3.5 mm jack',
  unknown: 'Not reported by the browser',
};

export function buildReport(r) {
  const L = [];
  L.push('HEADSET TEST REPORT - headsetplus.com/pages/headset-test');
  L.push(`Date: ${r.date}`);
  L.push(`Browser: ${r.ua}`);
  L.push('');
  if (r.device) {
    L.push(`Microphone: ${r.device.raw}`);
    L.push(`  Brand: ${r.device.brand || 'unknown'}   Connection: ${CONNECTION_TEXT[r.device.connection]}` + (r.device.vid ? `   USB id: ${r.device.vid}:${r.device.pid}` : ''));
  }
  if (r.settings) {
    const s = r.settings;
    L.push(`  Sample rate: ${s.sampleRate || 'n/a'} Hz   Channels: ${s.channelCount || 'n/a'}   Echo cancellation: ${fmtBool(s.echoCancellation)}   Noise suppression: ${fmtBool(s.noiseSuppression)}   Auto gain: ${fmtBool(s.autoGainControl)}`);
  }
  if (r.output) L.push(`Output: ${r.output}`);
  L.push('');
  for (const row of r.rows) {
    if (row.status === 'skip') continue;
    L.push(`[${row.status.toUpperCase()}] ${row.name}: ${row.value}`);
    if (row.fix) L.push(`       ${row.fix}`);
  }
  L.push('');
  L.push(`Overall: ${{ pass: 'Call-ready', warn: 'Works, with issues', fail: 'Needs attention' }[r.verdict]}`);
  L.push('Measured with browser echo cancellation, noise suppression and auto gain turned off.');
  return L.join('\n');
}

function fmtBool(v) {
  return v === true ? 'on' : v === false ? 'off' : 'n/a';
}

/* ------------------------------------------------------------------ */
/* Browser element                                                     */
/* ------------------------------------------------------------------ */

const WORKLET_SRC = `
class HspCapture extends AudioWorkletProcessor {
  constructor() {
    super();
    this.on = false; this.buf = new Float32Array(2048); this.n = 0; this.first = -1;
    this.id = 0;
    this.port.onmessage = (e) => {
      if (e.data.cmd === 'start') { this.id = e.data.id; this.on = true; this.n = 0; this.first = -1; }
      else if (e.data.cmd === 'stop' && e.data.id === this.id && this.on) { this.flush(); this.on = false; this.port.postMessage({ t: 'stop', id: this.id }); }
    };
  }
  flush() { if (this.n) { this.port.postMessage({ t: 'data', id: this.id, d: this.buf.slice(0, this.n) }); this.n = 0; } }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (this.on && ch) {
      if (this.first < 0) { this.first = currentFrame; this.port.postMessage({ t: 'start', id: this.id, frame: currentFrame }); }
      for (let i = 0; i < ch.length; i++) { this.buf[this.n++] = ch[i]; if (this.n === this.buf.length) this.flush(); }
    }
    return true;
  }
}
registerProcessor('hsp-capture', HspCapture);
`;

const SENTENCE = 'My headset sounds clear, and everyone on this call can hear me without an echo.';

// Tab tooltips: what the tab is for, in a few words. Anything longer does not get read.
const TABS = [
  { id: 'full', label: 'Full check', tip: 'All tests in one guided run, about a minute' },
  { id: 'mic', label: 'Mic & playback', tip: 'Record yourself and hear how you sound' },
  { id: 'compare', label: 'Compare two mics', tip: 'Hear two mics side by side' },
  { id: 'speakers', label: 'Speakers', tip: 'Check left and right sound' },
  { id: 'echo', label: 'Echo test', tip: 'Find out if callers hear an echo' },
];

const STATUS_TEXT = { pass: 'Good', warn: 'Check', fail: 'Fix', skip: 'Not run' };

// The full check, as the step list shows it: a name and what it answers, in a few words.
const STEPS = [
  ['Mic check', 'Is it the right mic?'],
  ['Room noise', 'What your mic hears in silence'],
  ['Your voice', 'Level and voice quality'],
  ['Speakers', 'Left and right'],
  ['Echo', 'Do callers hear themselves?'],
  ['Scorecard', 'Results and fixes'],
];

const ICONS = {
  mic: '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21"/>',
  speaker: '<path d="M11 5 6.5 9H3.5v6h3L11 19V5Z"/><path d="M15.5 9a4.5 4.5 0 0 1 0 6M18.5 6a8.5 8.5 0 0 1 0 12"/>',
  check: '<path d="m5 12.5 4.5 4.5L19 7.5"/>',
  alert: '<path d="M12 7.5v6M12 16.8v.2"/>',
  x: '<path d="M7.5 7.5l9 9M16.5 7.5l-9 9"/>',
  dash: '<path d="M7.5 12h9"/>',
  arrow: '<path d="M5 12h13M13 6.5l5.5 5.5-5.5 5.5"/>',
  play: '<path d="M8 5.8v12.4L18 12 8 5.8Z" fill="currentColor"/>',
  again: '<path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3M4.5 4.5v4h4"/>',
  copy: '<rect x="8.5" y="8.5" width="11" height="11" rx="2"/><path d="M15.5 8.5v-2a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2"/>',
  download: '<path d="M12 4.5v10M7.5 10.5 12 15l4.5-4.5M5 19.5h14"/>',
  ext: '<path d="M9 6.5h8.5V15M17.5 6.5 7 17"/>',
  up: '<path d="M7 11v9H4.5v-9H7Z"/><path d="M7 11l3.6-6.6c1.2 0 2.2 1 2 2.3L12.2 10h5.6a2 2 0 0 1 2 2.4l-1.3 6A2 2 0 0 1 16.5 20H7"/>',
  down: '<path d="M7 13V4H4.5v9H7Z"/><path d="M7 13l3.6 6.6c1.2 0 2.2-1 2-2.3L12.2 14h5.6a2 2 0 0 0 2-2.4l-1.3-6A2 2 0 0 0 16.5 4H7"/>',
  headset: '<path d="M4 14v-2a8 8 0 0 1 16 0v2"/><rect x="3" y="13" width="4.5" height="7" rx="2"/><rect x="16.5" y="13" width="4.5" height="7" rx="2"/>',
  laptop: '<rect x="4.5" y="5" width="15" height="10" rx="1.5"/><path d="M2.5 19h19"/>',
  mute: '<path d="M11 5 6.5 9H3.5v6h3L11 19V5Z"/><path d="m15.5 9.5 5 5M20.5 9.5l-5 5"/>',
  stop: '<rect x="7" y="7" width="10" height="10" rx="1.5" fill="currentColor"/>',
  record: '<circle cx="12" cy="12" r="5.5" fill="currentColor" stroke="none"/>',
  waves: '<path d="M3 12h2l2-5 3 10 3-12 3 10 2-3h3"/>',
};

function icon(name, size = 20) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.9');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.innerHTML = ICONS[name] || '';
  return svg;
}

const STATUS_ICON = { pass: 'check', warn: 'alert', fail: 'x', skip: 'dash' };

function el(tag, attrs, ...kids) {
  const e = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === null || v === undefined || v === false) continue;
      if (k === 'class') e.className = v;
      else if (k === 'text') e.textContent = v;
      else if (k.startsWith('on')) e.addEventListener(k.slice(2), v);
      else e.setAttribute(k, v === true ? '' : v);
    }
  }
  for (const c of kids.flat()) if (c !== null && c !== undefined && c !== false) e.append(c);
  return e;
}

/* Plain-language steps for turning a blocked microphone back on. The browser will not let a page
   do this for the visitor, so the page has to teach it. Keep each step to one action. */
const MIC_GUIDES = {
  chrome: { label: 'Chrome', steps: [
    'Look at the left end of the web address at the top. Click the small icon there (two sliders, or a padlock).',
    'Find "Microphone" in the list and switch it on.',
    'If Chrome shows a "Reload" button, click it, then press Start again. Otherwise just come back here.',
  ] },
  edge: { label: 'Edge', steps: [
    'Click the padlock at the left end of the web address at the top.',
    'Choose "Permissions for this site", then set "Microphone" to Allow.',
    'If Edge shows a "Reload" button, click it, then press Start again. Otherwise just come back here.',
  ] },
  firefox: { label: 'Firefox', steps: [
    'Click the small icon at the left end of the web address at the top (a crossed-out microphone, or two sliders).',
    'Next to "Use the Microphone", click the little X to remove the block.',
    'Reload the page and press Start. Firefox will ask again. Choose Allow.',
  ] },
  safari: { label: 'Safari on Mac', steps: [
    'In the menu bar at the very top of your screen, choose Safari, then Settings.',
    'Open the "Websites" tab and click "Microphone" on the left.',
    'Find headsetplus.com in the list and change it to Allow. Close Settings and come back here.',
  ] },
  ios: { label: 'iPhone or iPad', steps: [
    'Tap the "aA" at the left end of the web address (or the icon there).',
    'Tap "Website Settings", then set "Microphone" to Allow.',
    'Come back to this page. If nothing happens, reload it and press Start again.',
  ] },
  android: { label: 'Android', steps: [
    'Tap the icon at the left end of the web address at the top (a padlock or two sliders).',
    'Tap "Permissions", then "Microphone", and choose Allow.',
    'Come back to this page. If nothing happens, reload it. Still blocked? Open Settings, then Apps, your browser, Permissions, Microphone, and choose Allow.',
  ] },
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function ga(name, params) {
  try {
    if (window.HSPGA4 && typeof window.HSPGA4.send === 'function') window.HSPGA4.send(name, params || {});
  } catch (e) { /* analytics must never break the tool */ }
  // Microsoft Clarity, when the page loads it: tagging the recording with the same
  // event lets a session replay be found by "thumbs down" or "gave up at step 3".
  try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (e) { /* same */ }
}

// One-tap reasons after a thumbs down. 'other' opens an optional text box in the shared
// widget (assets/hsp-feedback.js), which scrubs emails and phone numbers before sending:
// GA4 forbids personal data, and a text box is where people type their email.
const FEEDBACK_REASONS = {
  test: [['not_detected', "Didn't find my headset"], ['confusing', 'Results were confusing'], ['did_not_work', "The test didn't work"], ['other', 'Something else']],
  page: [['no_answer', "Didn't answer my question"], ['hard_to_follow', 'Hard to follow'], ['other', 'Something else']],
};

function defineElement() {
  class HspHeadsetTest extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      // Usage-tracking state. Not this.track: that is the microphone MediaStreamTrack.
      this.usage = { startedAt: 0, lastStep: null, completed: false };
      this.mode = this.dataset.mode === 'mini' ? 'mini' : 'full';
      // Single-tool pages (/pages/speaker-test): the same engine, one tool, no tab bar.
      this.tool = this.mode === 'full' && this.dataset.tool === 'speakers' ? 'speakers' : '';
      this.cfg = {
        suggest: this.dataset.suggestUrl || '/search/suggest.json',
        fullUrl: this.dataset.fullUrl || '/pages/headset-test',
        brands: safeJson(this.dataset.brands),
        links: safeJson(this.dataset.links),
      };
      this.state = { results: {} };
      this.classList.add('is-enhanced');
      this.querySelectorAll('[data-ht-start]').forEach((b) => {
        b.disabled = false;
        b.addEventListener('click', () => this.start(b.dataset.htStart || 'full'));
      });
      this.addEventListener('click', (e) => {
        const a = e.target.closest('a[data-ht-cta]');
        if (a) ga('headset_test_cta', { target: a.dataset.htCta, mode: this.mode });
      });
      if (this.mode === 'full') this.setupPageTracking();
    }

    disconnectedCallback() {
      this.stopMic();
      if (this.ctx) this.ctx.close().catch(() => {});
    }

    /* ---------- audio plumbing ---------- */

    async ensureContext() {
      if (!this.ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) throw Object.assign(new Error('no audio'), { name: 'Unsupported' });
        this.ctx = new AC({ latencyHint: 'interactive' });
      }
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      return this.ctx;
    }

    async openMic(deviceId) {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw Object.assign(new Error('no getUserMedia'), { name: window.isSecureContext ? 'Unsupported' : 'Insecure' });
      }
      await this.ensureContext();
      this.stopMic();
      const audio = { echoCancellation: false, noiseSuppression: false, autoGainControl: false };
      if (deviceId) audio.deviceId = { exact: deviceId };
      const stream = await navigator.mediaDevices.getUserMedia({ audio });
      this.stream = stream;
      this.track = stream.getAudioTracks()[0];
      const ctx = this.ctx;
      this.src = ctx.createMediaStreamSource(stream);
      this.analyser = ctx.createAnalyser();
      this.analyser.fftSize = 2048;
      this.src.connect(this.analyser);
      await this.attachCapture();
      this.monitor = ctx.createGain();
      this.monitor.gain.value = 0;
      this.src.connect(this.monitor).connect(ctx.destination);
      this.device = parseDeviceLabel(this.track.label);
      this.settings = this.track.getSettings ? this.track.getSettings() : {};
      await this.listDevices();
      this.startMeter();
      this.lookupProduct();
      return stream;
    }

    stopMic() {
      if (this.meterRaf) cancelAnimationFrame(this.meterRaf);
      this.meterRaf = null;
      if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
      try { this.src && this.src.disconnect(); } catch (e) {}
      this.stream = null;
      this.src = null;
    }

    async attachCapture() {
      const ctx = this.ctx;
      const sink = ctx.createGain();
      sink.gain.value = 0;
      sink.connect(ctx.destination);
      if (!this.workletLoaded && ctx.audioWorklet) {
        try {
          const url = URL.createObjectURL(new Blob([WORKLET_SRC], { type: 'application/javascript' }));
          await ctx.audioWorklet.addModule(url);
          this.workletLoaded = true;
        } catch (e) { this.workletLoaded = false; }
      }
      if (this.workletLoaded) {
        const node = new AudioWorkletNode(ctx, 'hsp-capture', { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1, channelCountMode: 'explicit' });
        this.src.connect(node).connect(sink);
        // Every capture carries an id, and the processor answers only the current
        // one. Without it, a late 'stop' or data block from the previous recording
        // lands on the next one and ends it at zero samples.
        let seq = 0;
        this.capture = {
          start: (onStart, onData, onStop) => {
            const id = ++seq;
            node.port.onmessage = (e) => {
              if (e.data.id !== id) return;
              if (e.data.t === 'start') onStart(e.data.frame);
              else if (e.data.t === 'data') onData(e.data.d);
              else if (e.data.t === 'stop') onStop();
            };
            node.port.postMessage({ cmd: 'start', id });
            return id;
          },
          stop: () => node.port.postMessage({ cmd: 'stop', id: seq }),
        };
      } else {
        // Older Safari: ScriptProcessorNode is deprecated but universally available.
        const sp = ctx.createScriptProcessor(4096, 1, 1);
        let handlers = null;
        let first = true;
        sp.onaudioprocess = (e) => {
          if (!handlers) return;
          if (first) { first = false; handlers.onStart(Math.round(e.playbackTime * ctx.sampleRate)); }
          handlers.onData(new Float32Array(e.inputBuffer.getChannelData(0)));
        };
        this.src.connect(sp).connect(sink);
        this.capture = {
          start: (onStart, onData, onStop) => { first = true; handlers = { onStart, onData, onStop }; },
          stop: () => { const h = handlers; handlers = null; if (h) h.onStop(); },
        };
      }
    }

    /** Records `seconds` of mic audio. Resolves { data, startFrame, sr }. */
    record(seconds, onProgress) {
      return new Promise((resolve) => {
        const sr = this.ctx.sampleRate;
        const want = Math.round(seconds * sr);
        const chunks = [];
        let got = 0;
        let startFrame = 0;
        let done = false;
        let stopped = false;
        const stop = () => { if (!stopped) { stopped = true; this.capture.stop(); } };
        const finish = () => {
          if (done) return;
          done = true;
          const data = new Float32Array(Math.min(got, want));
          let o = 0;
          for (const c of chunks) {
            const take = Math.min(c.length, data.length - o);
            if (take <= 0) break;
            data.set(c.subarray(0, take), o);
            o += take;
          }
          resolve({ data, startFrame, sr });
        };
        this.capture.start(
          (f) => { startFrame = f; },
          (d) => {
            chunks.push(d);
            got += d.length;
            if (onProgress) onProgress(Math.min(1, got / want));
            if (got >= want) stop();
          },
          finish,
        );
        // Safety net if the audio thread stalls (tab hidden, device unplugged).
        setTimeout(() => { if (!done) { stop(); setTimeout(finish, 300); } }, seconds * 1000 + 4000);
      });
    }

    playBuffer(samples, { pan = null, loop = false, gain = 1, invertRight = false } = {}) {
      const ctx = this.ctx;
      const b = ctx.createBuffer(invertRight ? 2 : 1, samples.length, ctx.sampleRate);
      b.getChannelData(0).set(samples);
      if (invertRight) {
        const r = b.getChannelData(1);
        for (let i = 0; i < samples.length; i++) r[i] = -samples[i];
      }
      const s = ctx.createBufferSource();
      s.buffer = b;
      s.loop = loop;
      const g = ctx.createGain();
      g.gain.value = gain;
      let node = s.connect(g);
      if (pan !== null && ctx.createStereoPanner) {
        const p = ctx.createStereoPanner();
        p.pan.value = pan;
        node = node.connect(p);
      }
      node.connect(ctx.destination);
      return { source: s, gain: g };
    }

    async listDevices() {
      const all = await navigator.mediaDevices.enumerateDevices();
      this.inputs = all.filter((d) => d.kind === 'audioinput');
      this.outputs = all.filter((d) => d.kind === 'audiooutput');
      const def = this.inputs.find((d) => d.deviceId === 'default');
      this.defaultLabel = def ? def.label.replace(/^default\s*-\s*/i, '') : null;
      this.defaults = defaultCheck(this.inputs, this.outputs);
      this.renderDeviceBar();
    }

    /* ---------- meter ---------- */

    startMeter() {
      const buf = new Float32Array(this.analyser.fftSize);
      let hold = -100;
      let holdT = 0;
      let lastVoice = 0;
      const tick = (t) => {
        if (!this.analyser || !this.ui) return;
        this.analyser.getFloatTimeDomainData(buf);
        let s = 0;
        let pk = 0;
        for (let i = 0; i < buf.length; i++) { s += buf[i] * buf[i]; pk = Math.max(pk, Math.abs(buf[i])); }
        const db = Math.max(-80, toDb(Math.sqrt(s / buf.length)));
        const pdb = Math.max(-80, toDb(pk));
        if (pdb > hold || t - holdT > 1500) { hold = pdb; holdT = t; }
        const pct = (d) => `${Math.max(0, Math.min(100, ((d + 80) / 80) * 100))}%`;
        this.ui.meterFill.style.width = pct(db);
        this.ui.meterPeak.style.left = pct(hold);
        this.ui.meterText.textContent = `${Math.round(db)} dBFS`;
        this.ui.meter.classList.toggle('is-hot', pdb > -1);
        // "Hearing you" holds for 700 ms after the last loud frame, so it does not
        // flicker between words.
        if (db > -50) lastVoice = t;
        const on = t - lastVoice < 700;
        if (on !== this._voiceOn) {
          this._voiceOn = on;
          this.ui.voice.classList.toggle('is-on', on);
          this.ui.voiceText.textContent = on ? 'Hearing you' : 'Listening';
          if (on) this._heardVoice = true;
        }
        this.drawWave(buf);
        this.meterRaf = requestAnimationFrame(tick);
      };
      this.meterRaf = requestAnimationFrame(tick);
    }

    drawWave(buf) {
      const c = this.ui.wave;
      if (!c) return;
      const w = c.clientWidth;
      const h = c.clientHeight;
      if (!w || !h) return;
      const dpr = window.devicePixelRatio || 1;
      if (c.width !== Math.round(w * dpr)) { c.width = Math.round(w * dpr); c.height = Math.round(h * dpr); }
      const g = c.getContext('2d');
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, w, h);
      if (!this.waveColor) this.waveColor = getComputedStyle(this).getPropertyValue('--ht-wave').trim() || '#1B6DB5';
      g.strokeStyle = this.waveColor;
      g.lineWidth = 1.5;
      g.beginPath();
      const step = buf.length / w;
      // Display gain follows the signal (smoothed), so normal speech fills the
      // scope instead of drawing a flat line. The level bar stays absolute.
      let pk = 0;
      for (let i = 0; i < buf.length; i++) pk = Math.max(pk, Math.abs(buf[i]));
      this._wavePk = Math.max(0.02, (this._wavePk || 0.02) * 0.92 + pk * 0.08, pk * 0.6);
      const gain = 0.9 / this._wavePk;
      for (let x = 0; x < w; x++) {
        const v = Math.max(-1, Math.min(1, buf[Math.floor(x * step)] * gain));
        const y = h / 2 - v * (h / 2) * 0.95;
        if (x) g.lineTo(x, y); else g.moveTo(x, y);
      }
      g.stroke();
    }

    /* ---------- UI shell ---------- */

    async start(entry) {
      ga('headset_test_start', { mode: this.mode, entry, tool: this.tool || 'headset' });
      this.usage.startedAt = Date.now();
      this.buildUi();
      this.classList.add('is-started');
      // Keep the heading; only the start buttons have done their job.
      this.querySelectorAll('[data-ht-intro] .hsp-ht-actions, [data-ht-intro] .hsp-ht-privacy, [data-ht-intro] .hsp-ht-preview').forEach((n) => { n.hidden = true; });
      if (entry === 'speakers') {
        try { await this.ensureContext(); } catch (e) { return this.showError(e); }
        this.renderOutputOnly();
        return this.showTab('speakers');
      }
      this.setStep({ eyebrow: 'Getting started', title: 'Allow the microphone', text: 'Your browser will ask to use the microphone. Choose Allow. The audio stays on this page.', meter: false });
      try {
        await this.openMic();
        ga('mic_permission', { result: 'granted' });
      } catch (e) {
        ga('mic_permission', { result: 'error', error: e && e.name });
        return this.showError(e);
      }
      return this.afterMic();
    }

    /** What happens once the microphone is open, whether on the first try or after an unblock. */
    async afterMic() {
      if (this.mode === 'mini') return this.miniFlow();
      this.showTab('full');
      // On a phone the site header and breadcrumb push the first step below the
      // fold; bring the widget to the top once the visitor has chosen to start.
      if (innerWidth < 900) {
        const top = this.getBoundingClientRect().top;
        if (top > 16) scrollBy({ top: top - 12, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    }

    buildUi() {
      if (this.ui) return;
      const app = this.querySelector('[data-ht-app]');
      app.hidden = false;
      app.textContent = '';
      const ui = (this.ui = {});
      if (this.mode === 'full' && !this.tool) {
        ui.tabs = el('div', { class: 'hsp-ht-tabs', role: 'tablist', 'aria-label': 'Headset test tools' });
        // One tooltip for all tabs, positioned under the hovered one. It lives
        // outside the tab strip because the strip scrolls sideways on small
        // screens, and overflow would clip a tooltip drawn inside it.
        ui.tip = el('div', { class: 'hsp-ht-tip', role: 'tooltip', id: this.uid('tip'), hidden: true });
        ui.toolbar = el('div', { class: 'hsp-ht-toolbar' }, ui.tabs, ui.tip);
        TABS.forEach((t) => {
          const b = el('button', {
            type: 'button', role: 'tab', class: 'hsp-ht-tab', 'data-tab': t.id, 'aria-selected': 'false',
            'aria-describedby': ui.tip.id, text: t.label,
            onclick: () => {
              ga('headset_test_tab', { tab: t.id });
              this.showTab(t.id);
              // Touch has no hover: a tap shows the tip briefly.
              if (matchMedia('(hover: none)').matches) this.showTip(b, t.tip, 2200);
            },
          });
          b.addEventListener('mouseenter', () => this.showTip(b, t.tip));
          b.addEventListener('focus', () => this.showTip(b, t.tip));
          b.addEventListener('mouseleave', () => this.hideTip());
          b.addEventListener('blur', () => this.hideTip());
          ui.tabs.append(b);
        });
        ui.tabs.addEventListener('scroll', () => this.hideTip(), { passive: true });
        app.append(ui.toolbar);
      }

      // Device bar: which mic and speaker, and a line of facts about the mic.
      ui.devices = el('div', { class: 'hsp-ht-devices' });
      ui.strip = el('div', { class: 'hsp-ht-strip', hidden: true });
      ui.devbar = el('div', { class: 'hsp-ht-devbar' }, ui.devices, ui.strip);

      // Live meter.
      ui.wave = el('canvas', { class: 'hsp-ht-wave', 'aria-hidden': 'true' });
      ui.meterFill = el('span', { class: 'hsp-ht-meter-fill' });
      ui.meterPeak = el('span', { class: 'hsp-ht-meter-peak' });
      ui.meterText = el('span', { class: 'hsp-ht-meter-text', text: '-- dBFS' });
      ui.meter = el('div', { class: 'hsp-ht-meter', role: 'img', 'aria-label': 'Live microphone level' },
        el('span', { class: 'hsp-ht-meter-track' }, ui.meterFill, ui.meterPeak), ui.meterText);
      ui.voiceText = el('span', { text: 'Listening' });
      ui.voice = el('span', { class: 'hsp-ht-voice', 'aria-live': 'polite' }, el('span', { class: 'hsp-ht-voice-dot' }), ui.voiceText);
      ui.scope = el('div', { class: 'hsp-ht-scope', hidden: true },
        el('div', { class: 'hsp-ht-scope-top' }, el('span', { class: 'hsp-ht-scope-label' }, icon('mic', 16), 'Live mic level'), ui.voice),
        ui.wave, ui.meter);

      // Stage: the current step. Head (what to do), meter, body (answers, actions).
      ui.notice = el('div', { class: 'hsp-ht-notice', role: 'status', hidden: true });
      ui.stepHead = el('div', { class: 'hsp-ht-stephead', 'aria-live': 'polite' });
      ui.stepBody = el('div', { class: 'hsp-ht-stepbody' });
      ui.result = el('div', { class: 'hsp-ht-result', hidden: true });
      ui.stage = el('section', { class: 'hsp-ht-stage', 'aria-label': 'Current step' }, ui.notice, ui.stepHead, ui.scope, ui.stepBody, ui.result);

      // Step list for the full check: every step, what it answers, and where you are.
      ui.stepper = el('ol', { class: 'hsp-ht-stepper' });
      ui.aside = el('aside', { class: 'hsp-ht-aside', hidden: true, 'aria-label': 'Full check steps' },
        el('p', { class: 'hsp-ht-aside-title', text: 'Full check' }),
        el('p', { class: 'hsp-ht-aside-sub', text: '5 steps, about a minute' }),
        ui.stepper);
      ui.body = el('div', { class: 'hsp-ht-body' }, ui.stage, ui.aside);
      ui.context = el('div', { class: 'hsp-ht-context', hidden: true });
      app.append(ui.devbar, ui.body, ui.context);
    }

    /**
     * "Was this useful?" The shared widget (assets/hsp-feedback.js, loaded before this
     * module) does the UI; this supplies the events and context. Each step is sent the
     * moment it happens so it counts even if they leave: headset_test_helpful_yes / _no,
     * headset_test_feedback_reason {reason}, headset_test_feedback_text {feedback_text}.
     * Yes and No are separate event NAMES so the split shows in GA4 with no setup.
     * context: 'test' (under the result, once they finish) or 'page' (end of the copy).
     *
     * One question per visit: the first vote, in either place, removes every other
     * unanswered widget and stops new ones (a re-run's scorecard) from appearing. The
     * page slot hides itself once empty (.hsp-htc-feedback:empty).
     */
    feedbackEl(context) {
      const wrap = el('div', { class: 'hsp-ht-feedback' });
      if (!window.HSPFeedback || this._fbVoted) return null;
      this._fbOpen = (this._fbOpen || []).filter((w) => w.isConnected);
      this._fbOpen.push(wrap);
      return window.HSPFeedback.mount(wrap, {
        onVote: () => {
          this._fbVoted = true;
          this._fbOpen.forEach((w) => { if (w !== wrap) w.remove(); });
          this._fbOpen = [wrap];
        },
        question: context === 'page' ? 'Was this page helpful?' : 'Was this test useful?',
        reasonsQuestion: 'What went wrong?',
        reasons: FEEDBACK_REASONS[context === 'page' ? 'page' : 'test'],
        events: { yes: 'headset_test_helpful_yes', no: 'headset_test_helpful_no', reason: 'headset_test_feedback_reason', text: 'headset_test_feedback_text' },
        params: () => ({ context, verdict: (this.state && this.state.verdict) || 'none', tab: this.tab || 'none', mode: this.mode }),
        send: ga,
      });
    }

    /**
     * Page-level tracking for the full page: which copy sections come into view, which
     * FAQ or app guides get opened, the page feedback slot, and whether someone who
     * started the test left before finishing (and at which step).
     */
    setupPageTracking() {
      const slot = document.querySelector('[data-ht-page-feedback]');
      const pageFb = slot && !slot.childElementCount ? this.feedbackEl('page') : null;
      if (pageFb) slot.append(pageFb);
      if ('IntersectionObserver' in window) {
        const seen = new Set();
        const io = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            const h = en.target.querySelector('h2');
            const id = (h && h.id) || 'unknown';
            if (en.isIntersecting && !seen.has(id)) { seen.add(id); ga('headset_test_section_view', { section: id }); io.unobserve(en.target); }
          });
        }, { threshold: 0.4 });
        document.querySelectorAll('.hsp-htc-section').forEach((sec) => io.observe(sec));
      }
      document.querySelectorAll('.hsp-htc details').forEach((d) => {
        d.addEventListener('toggle', () => {
          if (!d.open) return;
          const q = ((d.querySelector('summary') || {}).textContent || '').trim().slice(0, 90);
          ga('headset_test_faq_open', { question: q });
        });
      });
      window.addEventListener('pagehide', () => {
        if (this.usage.startedAt && !this.usage.completed) {
          ga('headset_test_exit', { last_step: this.usage.lastStep || 'none', tab: this.tab || 'none', seconds: Math.round((Date.now() - this.usage.startedAt) / 1000) });
        }
      });
    }

    showTip(btn, text, ms) {
      const { tip, toolbar } = this.ui;
      if (!tip) return;
      clearTimeout(this._tipTimer);
      tip.textContent = text;
      tip.hidden = false;
      const bar = toolbar.getBoundingClientRect();
      const r = btn.getBoundingClientRect();
      const w = tip.offsetWidth;
      const centre = r.left - bar.left + r.width / 2;
      const left = Math.max(0, Math.min(bar.width - w, centre - w / 2));
      tip.style.left = `${Math.round(left)}px`;
      tip.style.setProperty('--ht-tip-arrow', `${Math.round(centre - left)}px`);
      if (ms) this._tipTimer = setTimeout(() => this.hideTip(), ms);
    }

    hideTip() {
      clearTimeout(this._tipTimer);
      if (this.ui && this.ui.tip) this.ui.tip.hidden = true;
    }

    showTab(id) {
      this.tab = id;
      this.stopPlayback();
      if (this.ui.tabs) {
        this.ui.tabs.querySelectorAll('[role=tab]').forEach((t) => t.setAttribute('aria-selected', String(t.dataset.tab === id)));
      }
      this.renderStepper(id === 'full' ? 0 : null);
      const needsMic = id !== 'speakers';
      if (needsMic && !this.stream) {
        this.setStep({
          eyebrow: 'Microphone needed',
          title: 'This test uses your microphone',
          text: 'Allow the microphone when your browser asks.',
          meter: false,
          actions: [{ label: 'Turn on the microphone', primary: true, onclick: async () => { try { await this.openMic(); this.showTab(id); } catch (e) { this.showError(e); } } }],
        });
        return;
      }
      this.ui.result.hidden = true;
      this.ui.context.hidden = true;
      ({ full: () => this.fullFlow(0), mic: () => this.micTool(), compare: () => this.compareTool(), speakers: () => this.speakerTool(), echo: () => this.echoTool() })[id]();
    }

    /** cur: 0-4 a step, 5 the scorecard, null hides the list (other tabs). */
    renderStepper(cur) {
      const { stepper, aside, body } = this.ui;
      const show = this.mode === 'full' && cur !== null && cur !== undefined;
      aside.hidden = !show;
      body.classList.toggle('has-aside', show);
      if (!show) return;
      this._stepIdx = cur;
      stepper.textContent = '';
      STEPS.forEach(([name, what], i) => {
        const state = i < cur ? 'done' : i === cur ? 'current' : 'todo';
        const last = i === STEPS.length - 1;
        stepper.append(el('li', { class: `hsp-ht-stepper-item is-${state}${last ? ' is-final' : ''}`, 'aria-current': state === 'current' ? 'step' : null },
          el('span', { class: 'hsp-ht-stepper-mark' }, state === 'done' ? icon('check', 16) : last ? icon('check', 16) : String(i + 1)),
          el('span', { class: 'hsp-ht-stepper-text' },
            el('span', { class: 'hsp-ht-stepper-name', text: name }),
            el('span', { class: 'hsp-ht-stepper-what', text: what }),
            state === 'done' ? el('span', { class: 'hsp-ht-visually-hidden', text: ' (done)' }) : null)));
      });
    }

    renderDeviceBar() {
      const ui = this.ui;
      if (!ui) return;
      ui.devices.textContent = '';
      if (this.inputs && this.inputs.length && this.stream) {
        // Opened without a deviceId, Chromium reports deviceId "default"; find the
        // real entry by name so the list shows the mic actually in use.
        const real = this.inputs.filter((d) => d.deviceId !== 'default' && d.deviceId !== 'communications');
        const byId = real.find((d) => this.settings && d.deviceId === this.settings.deviceId);
        const byName = this.device && real.find((d) => parseDeviceLabel(d.label).name === this.device.name);
        const current = (byId || byName || {}).deviceId;
        const sel = el('select', { class: 'hsp-ht-select', id: this.uid('mic'), 'aria-label': 'Microphone', onchange: async (e) => { ga('headset_test_mic_change', { tab: this.tab || 'full' }); try { await this.openMic(e.target.value); this.showTab(this.tab || 'full'); } catch (err) { this.showError(err); } } });
        real.forEach((d, i) => {
          sel.append(el('option', { value: d.deviceId, selected: d.deviceId === current, text: parseDeviceLabel(d.label).name || `Microphone ${i + 1}` }));
        });
        ui.devices.append(el('label', { class: 'hsp-ht-pick', for: sel.id, title: 'Microphone' }, icon('mic', 18), el('span', { class: 'hsp-ht-visually-hidden', text: 'Microphone' }), sel));
      }
      this.renderOutputSelect();
      this.renderCard();
      this.renderNotice();
    }

    /**
     * "Your headset is not the default." Teams and Zoom set to Default follow the
     * computer, so a webcam or laptop mic that won the default is the real call
     * problem even when the headset tests fine.
     */
    renderNotice() {
      const n = this.ui && this.ui.notice;
      if (!n) return;
      n.textContent = '';
      const dc = this.defaults;
      const p = dc ? defaultProblems(dc) : { any: false };
      if (!p.any) { n.hidden = true; return; }
      n.hidden = false;
      const hs = (dc.mic.headset || dc.spk.headset).p.name;
      const what = { webcam: 'your webcam', builtin: "your computer's built-in mic", other: 'another device' };
      const spkWhat = { builtin: "your computer's speakers", webcam: "your webcam's speaker" };
      const lines = [];
      if (p.mic) lines.push(`Your default mic is ${what[dc.mic.defKind] || 'another device'} (${dc.mic.def.name}), not your ${hs}.`);
      if (p.comm) lines.push(`Windows sends calls to ${dc.mic.comm.name}, not your ${hs}.`);
      if (p.spk) lines.push(`Your default speaker is ${spkWhat[dc.spk.defKind] || dc.spk.def.name}, not your headset.`);
      lines.push('Calls set to "Default" use these.');
      const testingHeadset = this.device && deviceKind(this.device) === 'headset';
      const steps = el('ol', { class: 'hsp-ht-notice-steps', hidden: true }, defaultSteps(p).map((t) => el('li', { text: t })));
      const actions = [];
      if (p.mic && !testingHeadset && dc.mic.headset) {
        actions.push(el('button', { type: 'button', class: 'hsp-ht-btn hsp-ht-btn--small', text: 'Test my headset instead', onclick: async () => {
          ga('headset_test_default', { action: 'switch' });
          try { await this.openMic(dc.mic.headset.id); this.showTab(this.tab || 'full'); } catch (e) { this.showError(e); }
        } }));
      }
      actions.push(el('button', { type: 'button', class: 'hsp-ht-btn hsp-ht-btn--small', 'aria-expanded': 'false', text: 'Make it the default', onclick: (e) => {
        steps.hidden = !steps.hidden;
        e.target.setAttribute('aria-expanded', String(!steps.hidden));
        if (!steps.hidden) ga('headset_test_default', { action: 'steps' });
      } }));
      n.append(
        el('p', { class: 'hsp-ht-notice-title', text: p.mic || p.comm ? 'Your headset is not the default mic' : 'Your headset is not the default speaker' }),
        el('p', { class: 'hsp-ht-notice-text', text: lines.join(' ') }),
        el('div', { class: 'hsp-ht-actions' }, actions),
        steps,
      );
      if (!this._noticeSent) { this._noticeSent = true; ga('headset_test_default', { action: 'shown', mic: p.mic, spk: p.spk, comm: p.comm, kind: dc.mic.defKind || 'none' }); }
    }

    renderOutputOnly() {
      this.ui.devices.textContent = '';
      this.renderOutputSelect();
    }

    renderOutputSelect() {
      const ui = this.ui;
      const canSink = this.ctx && typeof this.ctx.setSinkId === 'function';
      const outs = (this.outputs || []).filter((d) => d.deviceId !== 'default' && d.deviceId !== 'communications' && d.label);
      if (canSink && outs.length) {
        const sel = el('select', { class: 'hsp-ht-select', id: this.uid('out'), 'aria-label': 'Speaker', onchange: async (e) => { try { await this.ctx.setSinkId(e.target.value); this.outputLabel = e.target.selectedOptions[0].textContent; } catch (err) { /* keep default */ } } });
        sel.append(el('option', { value: '', text: 'System default speaker' }));
        outs.forEach((d) => sel.append(el('option', { value: d.deviceId, text: parseDeviceLabel(d.label).name })));
        ui.devices.append(el('label', { class: 'hsp-ht-pick', for: sel.id, title: 'Speaker' }, icon('speaker', 18), el('span', { class: 'hsp-ht-visually-hidden', text: 'Speaker' }), sel));
      } else {
        const name = el('span', { text: 'System default speaker' });
        ui.devices.append(el('span', { class: 'hsp-ht-pick hsp-ht-pick--static', title: 'Change the speaker in your computer\u2019s sound settings' }, icon('speaker', 18), name));
        // Speaker page: device names are hidden until the visitor allows the microphone, and
        // this page never asks for it. selectAudioOutput() (Chrome/Edge on a computer) shows
        // the browser's own output picker with no microphone prompt and hands back the choice.
        const canPick = this.tool && canSink && navigator.mediaDevices && typeof navigator.mediaDevices.selectAudioOutput === 'function';
        if (canPick) {
          ui.devices.append(el('button', {
            type: 'button', class: 'hsp-ht-linkbtn hsp-ht-choose',
            onclick: async () => {
              try {
                const d = await navigator.mediaDevices.selectAudioOutput();
                await this.ctx.setSinkId(d.deviceId);
                this.outputLabel = d.label;
                name.textContent = (d.label && parseDeviceLabel(d.label).name) || d.label || 'Chosen speaker';
                ga('headset_test_tool', { tool: 'speakers', action: 'choose_output' });
              } catch (err) { /* picker closed, or not allowed: keep the default */ }
            },
            text: 'Choose speaker',
          }));
        }
      }
    }

    /**
     * The side of the device bar: only the small link to the detected product. The fact
     * chips (brand, connection, sample rate, channels) were removed 2026-10-05 so the test
     * stays the focus; the downloaded report still lists them.
     */
    renderCard() {
      const strip = this.ui.strip;
      strip.textContent = '';
      strip.hidden = !(this.device && this.product);
      if (!strip.hidden) strip.append(this.productLink());
    }

    productLink() {
      const p = this.product;
      return el('a', { class: 'hsp-ht-product', href: p.url, 'data-ht-cta': 'detected-product', title: p.title },
        p.image ? el('img', { src: p.image, alt: '', width: '20', height: '20', loading: 'lazy', class: 'loaded' }) : null,
        el('span', { text: p.brandOnly ? `More from ${this.device.brand}` : 'View this headset' }),
        icon('arrow', 12));
    }

    /**
     * Renders the current step.
     *   eyebrow/title/text  what to do, in the head above the meter
     *   meter               show the live meter (default when a mic is open)
     *   listen              show "Hearing you / Listening" on the meter
     *   countdown           seconds; a ring counts down (setProgress drives it)
     *   progress            a plain bar instead of a ring
     *   choices             { label, options: [{ label, onclick }] } answers
     *   actions             buttons; the first primary is the orange one
     */
    setStep({ eyebrow, title, text, quote, extra, actions, progress, countdown, choices, meter = true, listen = false } = {}) {
      const { stepHead: h, stepBody: b } = this.ui;
      h.textContent = '';
      b.textContent = '';
      if (eyebrow) h.append(el('p', { class: 'hsp-ht-eyebrow', text: eyebrow }));
      if (title) h.append(el('h2', { class: 'hsp-ht-step-title', text: title }));
      if (text) h.append(el('p', { class: 'hsp-ht-step-text', text }));
      // The sentence to read sits above the meter: you read it, then watch the bar.
      if (quote) h.append(el('blockquote', { class: 'hsp-ht-sentence', text: quote }));
      this.ui.scope.hidden = !(meter && this.stream && this.tab !== 'speakers');
      this.ui.voice.hidden = !listen;
      if (extra) b.append(extra);
      this.ui.bar = null;
      this.ui.ring = null;
      if (countdown) {
        const C = 2 * Math.PI * 26;
        const wrap = el('div', { class: 'hsp-ht-countdown', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': '0' });
        wrap.innerHTML = `<svg viewBox="0 0 64 64" width="64" height="64" aria-hidden="true"><circle cx="32" cy="32" r="26" class="hsp-ht-ring-track"/><circle cx="32" cy="32" r="26" class="hsp-ht-ring-fill" stroke-dasharray="${C}" stroke-dashoffset="${C}"/></svg>`;
        const num = el('span', { class: 'hsp-ht-countdown-num', text: String(countdown) });
        wrap.append(num, el('span', { class: 'hsp-ht-countdown-label', text: 'seconds left' }));
        b.append(wrap);
        this.ui.ring = { wrap, circle: wrap.querySelector('.hsp-ht-ring-fill'), num, total: countdown, C };
      } else if (progress !== undefined) {
        this.ui.bar = el('span', { class: 'hsp-ht-progress-fill' });
        b.append(el('div', { class: 'hsp-ht-progress', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100' }, this.ui.bar));
      }
      // A step with a main action (Play sound) shows it before the answers; a step
      // that is only asking (Where did you hear the tone?) shows the answers first.
      let choiceNode = null;
      let actionNode = null;
      if (choices) {
        // Answers are cards, not buttons: with an icon they read as "pick one", never as
        // "go to the next step".
        choiceNode = el('div', { class: 'hsp-ht-choices', role: 'group', 'aria-label': choices.label },
          el('p', { class: 'hsp-ht-choices-label', text: choices.label }),
          el('div', { class: 'hsp-ht-choices-row' }, choices.options.map((o) =>
            el('button', { type: 'button', class: o.icon ? 'hsp-ht-choice hsp-ht-choice--card' : 'hsp-ht-choice', onclick: o.onclick },
              o.icon ? el('span', { class: 'hsp-ht-choice-icon' }, icon(o.icon, 24)) : null,
              el('span', { text: o.label })))));
      }
      if (actions && actions.length) actionNode = this.actionsEl(actions);
      const leadWithAction = actions && actions.some((a) => a.primary);
      (leadWithAction ? [actionNode, choiceNode] : [choiceNode, actionNode]).forEach((n) => { if (n) b.append(n); });
      if (actionNode) {
        const first = b.querySelector('.hsp-ht-btn--primary');
        if (first && this._focusSteps) first.focus({ preventScroll: true });
      }
      this._focusSteps = true;
    }

    /**
     * One main button, everything else small (operator, 2026-10-05). The first `primary`
     * action is the big orange button. Every other action is a small "tool" pill, an icon
     * plus one word (`short`), pushed to the right so it never competes with the step.
     * The full `label` stays as the pill's tooltip and accessible name.
     *   action: { label, short?, icon?, primary?, pressed?, disabled?, onclick }
     */
    actionsEl(actions) {
      let primaryUsed = false;
      const main = [];
      const tools = [];
      actions.filter(Boolean).forEach((a) => {
        if (a.primary && !primaryUsed) {
          primaryUsed = true;
          const trail = /^(Next|Start|Turn on|Continue)/.test(a.label) ? icon('arrow', 18) : null;
          main.push(el('button', { type: 'button', class: 'hsp-ht-btn hsp-ht-btn--primary', onclick: a.onclick, disabled: a.disabled },
            a.icon ? icon(a.icon, 18) : null, el('span', { text: a.label }), trail));
        } else {
          tools.push(this.toolEl(a));
        }
      });
      return el('div', { class: 'hsp-ht-actions' }, main, tools.length ? el('div', { class: 'hsp-ht-tools' }, tools) : null);
    }

    toolEl(a) {
      const name = a.icon
        || (/^Play/.test(a.label) ? 'play'
          : /again$/i.test(a.label) ? 'again'
          : /^(Download|Save)/.test(a.label) ? 'download'
          : /^Stop/.test(a.label) ? 'stop'
          : /^Copy/.test(a.label) ? 'copy' : null);
      const named = a.short && a.short !== a.label;
      return el('button', {
        type: 'button',
        class: `hsp-ht-tool${name ? ` hsp-ht-tool--${name}` : ''}`,
        onclick: a.onclick,
        disabled: a.disabled,
        title: named ? a.label : null,
        'aria-label': named ? a.label : null,
        'aria-pressed': a.pressed === undefined ? null : String(!!a.pressed),
      }, name ? icon(name, 14) : null, el('span', { text: a.short || a.label }));
    }

    /**
     * The headset picture: a headband, two ear cups and a "Both" pill, each a real button.
     *   mode 'play'   (Speakers tab): a cup plays its own side and lights while it sounds.
     *   mode 'answer' (full check): the cups ARE the answers to "where did you hear it?",
     *                 so no side ever lights (that would give the answer away); the
     *                 headband glows while the sound plays instead.
     * onSide(side) gets 'left' | 'right' | 'both'. flash(side) marks what is playing.
     */
    headsetEl(onSide, mode) {
      const wrap = el('div', { class: `hsp-ht-headset hsp-ht-headset--${mode}`, role: 'group', 'aria-label': mode === 'answer' ? 'Where did you hear it?' : 'Play a sound on one side' });
      const cup = (side, text) => el('button', { type: 'button', class: `hsp-ht-cup hsp-ht-cup--${side}`, 'data-side': side, onclick: () => onSide(side) },
        el('span', { class: 'hsp-ht-cup-label', text }));
      wrap.append(
        el('span', { class: 'hsp-ht-headset-band', 'aria-hidden': 'true' }),
        cup('left', 'Left'),
        el('button', { type: 'button', class: 'hsp-ht-cup-both', 'data-side': 'both', onclick: () => onSide('both') }, el('span', { text: 'Both' })),
        cup('right', 'Right'));
      wrap.flash = (side) => {
        const lit = mode === 'answer' ? [wrap] : [...wrap.querySelectorAll('[data-side]')].filter((b) => side === 'both' || b.dataset.side === side);
        wrap.querySelectorAll('.is-playing').forEach((n) => n.classList.remove('is-playing'));
        wrap.classList.remove('is-playing');
        lit.forEach((n) => n.classList.add('is-playing'));
        clearTimeout(wrap._t);
        wrap._t = setTimeout(() => { lit.forEach((n) => n.classList.remove('is-playing')); }, 1200);
      };
      return wrap;
    }

    /**
     * The speaker picture for /pages/speaker-test (data-tool="speakers"): two speaker
     * cabinets and a Both button, same contract as headsetEl in 'play' mode so the
     * callers do not care which they get. onSide(side) gets 'left' | 'right' | 'both'.
     */
    speakersEl(onSide) {
      const wrap = el('div', { class: 'hsp-ht-speakers', role: 'group', 'aria-label': 'Play a sound on one side' });
      const spk = (side, text) => el('button', { type: 'button', class: `hsp-ht-spk hsp-ht-spk--${side}`, 'data-side': side, onclick: () => onSide(side) },
        el('span', { class: 'hsp-ht-spk-label', text }));
      wrap.append(
        spk('left', 'Left'),
        el('button', { type: 'button', class: 'hsp-ht-cup-both', 'data-side': 'both', onclick: () => onSide('both') }, el('span', { text: 'Both' })),
        spk('right', 'Right'));
      wrap.flash = (side) => {
        const lit = [...wrap.querySelectorAll('[data-side]')].filter((b) => side === 'both' || b.dataset.side === side);
        wrap.querySelectorAll('.is-playing').forEach((n) => n.classList.remove('is-playing'));
        lit.forEach((n) => n.classList.add('is-playing'));
        clearTimeout(wrap._t);
        wrap._t = setTimeout(() => { lit.forEach((n) => n.classList.remove('is-playing')); }, 1200);
      };
      return wrap;
    }

    setProgress(f) {
      const r = this.ui.ring;
      if (r) {
        r.circle.setAttribute('stroke-dashoffset', String(r.C * (1 - f)));
        r.num.textContent = String(Math.max(0, Math.ceil(r.total * (1 - f) - 0.05)));
        r.wrap.setAttribute('aria-valuenow', String(Math.round(f * 100)));
        return;
      }
      if (!this.ui.bar) return;
      this.ui.bar.style.width = `${Math.round(f * 100)}%`;
      this.ui.bar.parentNode.setAttribute('aria-valuenow', String(Math.round(f * 100)));
    }

    uid(k) {
      this._uid = this._uid || Math.random().toString(36).slice(2, 7);
      return `hsp-ht-${k}-${this._uid}`;
    }

    stopPlayback() {
      (this.playing || []).forEach((p) => { try { p.source.stop(); } catch (e) {} });
      this.playing = [];
      if (this.sweepTimer) cancelAnimationFrame(this.sweepTimer);
    }

    playRecording(data) {
      this.stopPlayback();
      const p = this.playBuffer(data);
      this.playing = [p];
      p.source.start();
    }

    downloadWav(data, name) {
      const blob = new Blob([encodeWav(data, this.ctx.sampleRate)], { type: 'audio/wav' });
      const a = el('a', { href: URL.createObjectURL(blob), download: name });
      document.body.append(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
    }

    /* ---------- errors ---------- */

    showError(e) {
      this.buildUi();
      const name = (e && e.name) || 'Error';
      ga('headset_test_error', { error: name, mode: this.mode, tab: this.tab || 'none' });
      const ua = navigator.userAgent;
      const browser = /Edg\//.test(ua) ? 'edge' : /Firefox\//.test(ua) ? 'firefox' : /Chrome\//.test(ua) ? 'chrome' : /Safari\//.test(ua) ? 'safari' : 'other';
      const mac = /Mac OS X/.test(ua);
      const ios = /iPhone|iPad|iPod/.test(ua);
      const android = /Android/.test(ua);
      const osText = ios
        ? 'If that does not work, open Settings > Privacy & Security > Microphone on your device and switch your browser on.'
        : android
          ? 'If that does not work, open Settings > Apps > your browser > Permissions and allow the microphone.'
          : mac
            ? 'If that does not work, open System Settings > Privacy & Security > Microphone and switch your browser on.'
            : 'If that does not work, open Windows Settings > Privacy & security > Microphone and turn on "Let desktop apps access your microphone".';
      const blocked = name === 'NotAllowedError' || name === 'SecurityError';
      const blockedText = 'Your browser is stopping this page from using the microphone, so the test cannot hear you. It takes a minute to fix, and nothing you say leaves this page.';
      const copy = {
        NotAllowedError: ['The microphone is blocked', blockedText],
        SecurityError: ['The microphone is blocked', blockedText],
        NotSupportedError: ['This page cannot use the microphone here', 'Open headsetplus.com/pages/headset-test directly in Chrome, Edge, Firefox or Safari. If you came from inside another app (an email, a chat or a translator), open the link in your browser instead.'],
        NotFoundError: ['No microphone found', 'Your computer does not see a microphone. Plug the headset in (or pair it), wait a few seconds, then try again. For a USB headset, try a different port.'],
        OverconstrainedError: ['That microphone went away', 'It may have been unplugged. Choose another microphone or plug it back in.'],
        NotReadableError: ['Another app is using the microphone', 'Teams, Zoom or another calling app may be holding it. Close or leave the call there, then try again.'],
        AbortError: ['Another app is using the microphone', 'Teams, Zoom or another calling app may be holding it. Close or leave the call there, then try again.'],
        Insecure: ['This page must load over https', 'Open headsetplus.com/pages/headset-test directly.'],
        Unsupported: ['This browser cannot run the test', 'Use a current version of Chrome, Edge, Firefox or Safari.'],
      }[name] || ['Something stopped the test', 'Reload the page and try again. If it keeps happening, try another browser.'];
      const actions = [blocked
        ? { label: 'I have allowed it, try again', primary: true, onclick: () => this.manualRetry() }
        : { label: 'Try again', primary: true, onclick: () => location.reload() }];
      // The speaker test needs no permission, so a blocked mic is never a dead end.
      if (this.mode === 'full' && name !== 'Unsupported' && name !== 'Insecure') {
        actions.push({ label: 'Test speakers instead', icon: 'speaker', onclick: async () => {
          try { await this.ensureContext(); this.renderOutputOnly(); this.showTab('speakers'); } catch (err) { /* stay on this screen */ }
        } });
      }
      this.setStep({
        eyebrow: 'Something needs fixing', title: copy[0], text: copy[1], actions, meter: false,
        extra: blocked ? this.unblockGuide({ browser, ios, android, ua, osText }) : null,
      });
      if (blocked) this.watchMic();
    }

    /* ---------- unblocking a blocked microphone ---------- */

    /** Which set of steps fits this visitor: they can switch it, but the first guess is usually right. */
    micPlatform({ browser, ios, android }) {
      if (ios) return 'ios';
      if (android) return 'android';
      if (browser === 'safari') return 'safari';
      if (browser === 'firefox') return 'firefox';
      if (browser === 'edge') return 'edge';
      return 'chrome';
    }

    unblockGuide(env) {
      let current = this.micPlatform(env);
      const inApp = /FBAN|FBAV|Instagram|Line\/|MicroMessenger|LinkedInApp|Snapchat|GSA\/|; wv\)/.test(env.ua);
      const steps = el('ol', { class: 'hsp-ht-unblock-steps' });
      const chips = el('div', { class: 'hsp-ht-unblock-chips', role: 'group', 'aria-label': 'Choose your device or browser' });
      const draw = () => {
        steps.textContent = '';
        MIC_GUIDES[current].steps.forEach((t) => steps.append(el('li', { text: t })));
        [...chips.children].forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.key === current)));
      };
      Object.keys(MIC_GUIDES).forEach((key) => {
        chips.append(el('button', {
          type: 'button', class: 'hsp-ht-unblock-chip', 'data-key': key, 'aria-pressed': 'false', text: MIC_GUIDES[key].label,
          onclick: () => { current = key; draw(); ga('headset_test_unblock_guide', { context: key }); },
        }));
      });
      draw();
      this._unblockStatus = el('p', { class: 'hsp-ht-unblock-status', role: 'status' },
        el('span', { class: 'hsp-ht-unblock-pulse', 'aria-hidden': 'true' }),
        el('span', { class: 'hsp-ht-unblock-status-text', text: 'Waiting for you to allow it. This page carries on by itself the moment you do.' }));
      return el('div', { class: 'hsp-ht-unblock' },
        inApp ? el('p', { class: 'hsp-ht-unblock-inapp', text: 'It looks like you opened this from inside another app. Those often cannot use the microphone at all. Open this page in your normal browser (Chrome, Safari or Edge) and press Start there.' }) : null,
        el('p', { class: 'hsp-ht-unblock-label', text: 'Pick your device or browser' }),
        chips, steps, this._unblockStatus,
        el('details', { class: 'hsp-ht-unblock-more' },
          el('summary', { text: 'Still blocked after that?' }),
          el('p', { text: env.osText }),
          el('p', { text: 'Also check the headset itself: a mute switch, or a boom arm flipped up, can silence it.' })));
    }

    /** Continue on our own the moment the browser says the microphone is allowed. */
    watchMic() {
      this.unwatchMic();
      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: 'microphone' }).then((st) => {
          this._permStatus = st;
          st.onchange = () => { if (st.state !== 'denied') this.retryMic('auto'); };
        }).catch(() => {});
      }
      // Safari and iPhone do not announce the change. Coming back to the tab is the cue.
      this._onVisible = () => { if (document.visibilityState === 'visible') this.retryMic('return'); };
      document.addEventListener('visibilitychange', this._onVisible);
    }

    unwatchMic() {
      if (this._permStatus) this._permStatus.onchange = null;
      this._permStatus = null;
      if (this._onVisible) document.removeEventListener('visibilitychange', this._onVisible);
      this._onVisible = null;
    }

    /** Try the microphone again without an error screen. Resolves true if it opened. */
    async retryMic(source) {
      if (this._retrying || this.stream) return !!this.stream;
      this._retrying = true;
      try {
        await this.openMic();
      } catch (e) {
        this._retrying = false;
        return false;
      }
      this._retrying = false;
      this.unwatchMic();
      ga('mic_permission', { result: 'granted' });
      ga('headset_test_unblocked', { context: source });
      await this.afterMic();
      return true;
    }

    async manualRetry() {
      ga('headset_test_unblock_retry', {});
      const ok = await this.retryMic('button');
      if (ok || !this._unblockStatus) return;
      const t = this._unblockStatus.querySelector('.hsp-ht-unblock-status-text');
      if (t) t.textContent = 'Still blocked. Your browser says the microphone is off for this page, so check the steps above. If you changed it and nothing happens, reload the page and press Start again.';
      this._unblockStatus.classList.add('is-still');
      if (!this.querySelector('.hsp-ht-unblock-reload')) {
        this._unblockStatus.after(el('button', { type: 'button', class: 'hsp-ht-btn hsp-ht-btn--ghost hsp-ht-unblock-reload', onclick: () => location.reload(), text: 'Reload the page' }));
      }
    }

    /* ---------- product lookup ---------- */

    async lookupProduct() {
      const d = this.device;
      this.product = null;
      if (!d || !d.brand || d.connection === 'builtin' || deviceKind(d) === 'webcam') { this.renderCard(); return; }
      const toks = modelTokens(d.name);
      if (!toks.length) {
        if (this.cfg.brands[d.brand]) {
          this.product = { title: `${d.brand} headsets`, url: this.cfg.brands[d.brand], image: null, brandOnly: true };
          this.renderCard();
        }
        return;
      }
      // Show the brand link straight away; the model link replaces it when the
      // search answers. A slow or blocked request then still leaves a useful link.
      if (this.cfg.brands[d.brand]) {
        this.product = { title: `${d.brand} headsets`, url: this.cfg.brands[d.brand], image: null, brandOnly: true };
        this.renderCard();
      }
      const q = d.name.toLowerCase().includes(d.brand.toLowerCase()) ? d.name : `${d.brand} ${d.name}`;
      const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = setTimeout(() => ctl && ctl.abort(), 5000);
      try {
        const url = `${this.cfg.suggest}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=last`;
        const r = await fetch(url, { signal: ctl ? ctl.signal : undefined, headers: { Accept: 'application/json' } });
        if (!r.ok) return;
        const j = await r.json();
        const list = (j.resources && j.resources.results && j.resources.results.products) || [];
        const hit = list.find((p) => titleMatches(p.title, toks));
        if (hit) {
          this.product = { title: hit.title, url: String(hit.url).split('?')[0], image: hit.image || (hit.featured_image && hit.featured_image.url) || null };
          ga('headset_test_product_match', { brand: d.brand, match: 'model' });
        } else if (this.cfg.brands[d.brand]) {
          this.product = { title: `${d.brand} headsets`, url: this.cfg.brands[d.brand], image: null, brandOnly: true };
          ga('headset_test_product_match', { brand: d.brand, match: 'brand' });
        }
        if (this.product) this.renderCard();
      } catch (e) {
        /* no product link is the correct failure */
      } finally {
        clearTimeout(timer);
      }
    }

    /* ---------- full guided check ---------- */

    async fullFlow(step) {
      const r = this.state.results;
      this.renderStepper(Math.min(step, 5));
      this.usage.lastStep = STEPS[Math.min(step, 5)][0];
      ga('headset_test_step_view', { step: Math.min(step, 5) + 1, step_name: this.usage.lastStep });
      if (step === 0) {
        this.ui.result.hidden = true;
        this.ui.context.hidden = true;
        this.setStep({
          eyebrow: 'Step 1 of 5',
          title: 'Mic check',
          text: 'Say a few words. The bar should move when you talk.',
          listen: true,
          extra: el('p', { class: 'hsp-ht-hint', text: 'Bar not moving? Pick your headset in the microphone list above.' }),
          actions: [{ label: 'Next step', primary: true, onclick: () => this.fullFlow(1) }],
        });
        return;
      }
      if (step === 1) {
        this.setStep({ eyebrow: 'Step 2 of 5', title: 'Room noise', text: 'Stay quiet. We are measuring what your mic hears in silence.', countdown: 3 });
        await wait(600);
        const q = await this.record(3, (f) => this.setProgress(f));
        if (this.tab !== 'full') return;
        r.noiseDb = toDb(rms(q.data, Math.floor(q.data.length * 0.1)));
        ga('headset_test_step', { step: 'quiet', result: Math.round(r.noiseDb) });
        return this.fullFlow(2);
      }
      if (step === 2) {
        this.setStep({
          eyebrow: 'Step 3 of 5',
          title: 'Your voice',
          text: 'Read this out loud, the way you talk on calls.',
          quote: SENTENCE,
          listen: true,
          countdown: 6,
        });
        const s = await this.record(6, (f) => this.setProgress(f));
        if (this.tab !== 'full') return;
        r.speech = s.data;
        r.stats = speechStats(s.data, s.sr);
        r.bw = r.stats.speechDb > T.speechMissing ? analyseBandwidth(s.data, s.sr) : { cls: 'unknown', cutoffHz: null };
        ga('headset_test_step', { step: 'speech', result: r.bw.cls });
        const lvl = scoreLevel(r.stats);
        const bw = scoreBandwidth(r.bw, this.device);
        this.setStep({
          eyebrow: 'Step 3 of 5',
          title: 'How you sound',
          meter: false,
          extra: el('ul', { class: 'hsp-ht-rows hsp-ht-rows--inline' }, this.rowEl({ name: 'Level', ...lvl }), bw.status !== 'skip' ? this.rowEl({ name: 'Voice quality', ...bw }) : null),
          actions: [
            { label: 'Next step', primary: true, onclick: () => this.fullFlow(3) },
            { label: 'Play my recording', short: 'Play', onclick: () => this.playRecording(r.speech) },
            { label: 'Record again', short: 'Redo', icon: 'again', onclick: () => this.fullFlow(2) },
          ],
        });
        return;
      }
      if (step === 3) {
        r.speakers = {};
        return this.speakerRound('left', () => this.speakerRound('right', () => this.fullFlow(4)));
      }
      if (step === 4) {
        return this.echoRun((echo) => { r.echo = echo; this.fullFlow(5); }, 'Step 5 of 5');
      }
      if (step === 5) this.showResults();
    }

    /**
     * One side of the speaker check. The sound plays by itself as the step opens (the
     * click that got here is the user gesture audio needs), and the answer is a tap on
     * the headset picture. Answers are the same left/right/both/none as before.
     */
    speakerRound(side, next) {
      const r = this.state.results;
      const pan = side === 'left' ? -1 : 1;
      let pic = null;
      const play = () => {
        if (!pic || !pic.isConnected) return;
        this.stopPlayback();
        const p = this.playBuffer(pinkNoise(Math.round(this.ctx.sampleRate * 1.2), 0.2, side === 'left' ? 7 : 11), { pan });
        this.playing = [p];
        p.source.start();
        pic.flash(side);
      };
      const answer = (a) => { ga('headset_test_speaker_answer', { side, answer: a }); this.stopPlayback(); r.speakers[side] = a; next(); };
      pic = this.headsetEl(answer, 'answer');
      this.setStep({
        eyebrow: `Step 4 of 5 \u00b7 sound ${side === 'left' ? '1' : '2'} of 2`,
        title: 'Where did you hear it?',
        text: 'Listen for a short sound, then tap the side you heard it on.',
        meter: false,
        extra: pic,
        actions: [
          { label: 'Play the sound again', short: 'Replay', icon: 'again', onclick: play },
          { label: 'I heard nothing', icon: 'mute', onclick: () => answer('none') },
        ],
      });
      setTimeout(play, 450);
    }

    echoRun(done, eyebrow) {
      const tab = this.tab;
      const intro = () => this.setStep({
        eyebrow: eyebrow || 'Echo test',
        title: 'Echo',
        text: 'Keep the headset on at your normal volume. You will hear a short rising tone.',
        meter: false,
        actions: [{ label: 'Play the tone', primary: true, onclick: () => go() }],
      });
      const go = async () => {
        this.setStep({ eyebrow: eyebrow || 'Echo test', title: 'Listening for the tone', text: 'Stay quiet for a moment.', meter: false, progress: 0 });
        const ctx = this.ctx;
        const sr = ctx.sampleRate;
        const sweep = makeLogSweep(sr, 1.5, 300, 8000, 0.25);
        const recP = this.record(2.6, (f) => this.setProgress(f));
        await wait(250);
        const p = this.playBuffer(sweep);
        const when = ctx.currentTime + 0.15;
        p.source.start(when);
        this.playing = [p];
        const rec = await recP;
        if (this.tab !== tab) return;
        const x = xcorr(sweep, rec.data);
        const playFrame = Math.round(when * sr);
        const latencyMs = ((rec.startFrame + x.lag - playFrame) / sr) * 1000;
        const echo = { gain: x.gain, psr: x.psr, latencyMs };
        this.setStep({
          eyebrow: eyebrow || 'Echo test',
          title: 'Echo',
          meter: false,
          choices: { label: 'Where did you hear the tone?', options: [
            { label: 'In my headset', icon: 'headset', onclick: () => { echo.heard = 'headset'; ga('headset_test_step', { step: 'echo', result: scoreEcho(echo).status }); done(echo); } },
            { label: 'From my computer speakers', icon: 'laptop', onclick: () => { echo.heard = 'speakers'; done(echo); } },
            { label: 'I did not hear it', icon: 'mute', onclick: () => { echo.heard = 'none'; done(echo); } },
          ] },
          actions: [{ label: 'Play the tone again', short: 'Replay', icon: 'again', onclick: () => go() }],
        });
      };
      intro();
    }

    rowsFor(r) {
      return [
        { name: 'Right device', ...scoreDevice(this.device, this.defaultLabel) },
        { name: 'Level', ...scoreLevel(r.stats) },
        { name: 'Background noise', ...scoreNoise(r.noiseDb, r.stats && r.stats.speechDb) },
        { name: 'Voice quality', ...scoreBandwidth(r.bw, this.device) },
        { name: 'Echo', ...scoreEcho(r.echo) },
        { name: 'Speakers', ...scoreSpeakers(r.speakers) },
      ];
    }

    rowEl(row) {
      return el('li', { class: `hsp-ht-row is-${row.status}` },
        el('span', { class: 'hsp-ht-row-icon' }, icon(STATUS_ICON[row.status] || 'dash', 16)),
        el('span', { class: 'hsp-ht-row-body' },
          el('span', { class: 'hsp-ht-row-head' },
            el('strong', { text: row.name }),
            el('span', { class: 'hsp-ht-row-status', text: STATUS_TEXT[row.status] })),
          row.value ? el('span', { class: 'hsp-ht-row-value', text: row.value }) : null,
          row.fix ? el('span', { class: 'hsp-ht-row-fix', text: row.fix }) : null));
    }

    showResults() {
      const r = this.state.results;
      const rows = this.rowsFor(r);
      const v = verdict(rows);
      this.state.rows = rows;
      this.state.verdict = v;
      const st = (name) => (rows.find((x) => x.name === name) || {}).status || 'skip';
      this.usage.completed = true;
      ga('headset_test_complete', {
        verdict: v,
        bandwidth_class: (r.bw && r.bw.cls) || 'unknown',
        connection: this.device ? this.device.connection : 'unknown',
        brand: (this.device && this.device.brand) || 'unknown',
        row_device: st('Right device'), row_level: st('Level'), row_noise: st('Background noise'),
        row_voice: st('Voice quality'), row_echo: st('Echo'), row_speakers: st('Speakers'),
        duration_s: this.usage.startedAt ? Math.round((Date.now() - this.usage.startedAt) / 1000) : 0,
      });
      this.renderStepper(5);
      const shown = rows.filter((x) => x.status !== 'skip');
      const n = (st) => shown.filter((x) => x.status === st).length;
      const summary = [n('pass') && `${n('pass')} good`, n('warn') && `${n('warn')} to check`, n('fail') && `${n('fail')} to fix`].filter(Boolean).join(' \u00b7 ');
      const heading = { pass: 'Call-ready', warn: 'Works, with a few things to fix', fail: 'Needs attention before your next call' }[v];
      const box = this.ui.result;
      box.textContent = '';
      box.hidden = false;
      box.className = `hsp-ht-result is-${v}`;
      box.append(
        el('div', { class: 'hsp-ht-verdict-row' },
          el('span', { class: 'hsp-ht-verdict-icon' }, icon(STATUS_ICON[v], 26)),
          el('div', null, el('p', { class: 'hsp-ht-eyebrow', text: 'Your scorecard' }), el('h2', { class: 'hsp-ht-verdict', text: heading }), el('p', { class: 'hsp-ht-summary', text: summary }))),
        el('ul', { class: 'hsp-ht-rows' }, shown.map((x) => this.rowEl(x))),
        this.actionsEl([
          { label: 'Run the test again', primary: true, icon: 'again', onclick: () => { this.state.results = {}; this.fullFlow(0); } },
          { label: 'Copy report for IT', short: 'Copy report', onclick: (e) => this.copyReport(e.currentTarget.lastChild) },
          { label: 'Download report', short: 'Save report', onclick: () => this.downloadReport() },
          r.speech ? { label: 'Play my recording', short: 'Play', onclick: () => this.playRecording(r.speech) } : null,
        ]),
        this.feedbackEl('test'),
      );
      this.ui.stepHead.textContent = '';
      this.ui.stepBody.textContent = '';
      this.ui.scope.hidden = true;
      this.renderContext(rows, r);
      box.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
    }

    reportText() {
      return buildReport({
        date: new Date().toString(),
        ua: navigator.userAgent,
        device: this.device,
        settings: this.settings,
        output: this.outputLabel || 'System default',
        rows: this.state.rows || [],
        verdict: this.state.verdict || 'warn',
      });
    }

    async copyReport(btn) {
      const text = this.reportText();
      try {
        await navigator.clipboard.writeText(text);
        btn.textContent = 'Copied';
      } catch (e) {
        const ta = el('textarea', { class: 'hsp-ht-report', readonly: true, 'aria-label': 'Report text' });
        ta.value = text;
        (btn.closest('.hsp-ht-actions') || btn).after(ta);
        ta.select();
      }
      ga('headset_test_report', { action: 'copy' });
    }

    downloadReport() {
      const blob = new Blob([this.reportText()], { type: 'text/plain' });
      const a = el('a', { href: URL.createObjectURL(blob), download: 'headset-test-report.txt' });
      document.body.append(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
      ga('headset_test_report', { action: 'download' });
    }

    /** One contextual suggestion, chosen by the worst relevant finding. */
    renderContext(rows, r) {
      const box = this.ui.context;
      box.textContent = '';
      const L = this.cfg.links || {};
      const by = Object.fromEntries(rows.map((x) => [x.name, x.status]));
      let key = null;
      if (this.device && this.device.connection === 'builtin') key = 'noise';
      else if (r.bw && r.bw.cls === 'narrow') key = 'bluetooth';
      else if (by['Background noise'] === 'fail' || by['Background noise'] === 'warn') key = 'noise';
      else if (by['Echo'] === 'fail' && r.echo && r.echo.heard === 'headset') key = 'echo';
      this.querySelectorAll('[data-ht-rail]').forEach((n) => { n.hidden = true; });
      if (!key) { box.hidden = true; return; }
      const copy = {
        noise: ['A noise-canceling mic fixes this at the source', 'Boom mics built for offices reject the room before your calling app ever hears it.', L.noise, 'Shop noise-canceling headsets'],
        bluetooth: ['Stay in HD voice on a computer', 'A USB headset, or a Bluetooth headset with its own USB dongle, keeps your voice wideband when the mic is on.', L.usb, 'Shop USB headsets'],
        echo: ['Stop people hearing themselves', 'Our guide walks through the echo settings in Teams and the headset fixes that stop it.', L.echo, 'Read the echo guide'],
      }[key];
      if (!copy[2]) { box.hidden = true; return; }
      box.hidden = false;
      box.append(el('h2', { class: 'hsp-ht-context-title', text: copy[0] }), el('p', { text: copy[1] }));
      const rail = this.querySelector(`[data-ht-rail="${{ noise: 'noise', bluetooth: 'usb' }[key]}"]`);
      if (rail) { rail.hidden = false; box.append(rail); }
      box.append(el('a', { class: 'hsp-ht-cta', href: copy[2], 'data-ht-cta': key, text: copy[3] }));
    }

    /* ---------- standalone tools ---------- */

    micTool() {
      let last = null;
      // A toggle: aria-pressed carries on/off, the pill fills navy while it is on.
      const monitorBtn = () => ({ label: 'Hear myself', icon: 'headset', pressed: this.monitor.gain.value > 0, onclick: (e) => {
        const on = this.monitor.gain.value === 0;
        ga('headset_test_tool', { tool: 'mic', action: on ? 'monitor_on' : 'monitor_off' });
        this.monitor.gain.value = on ? 1 : 0;
        e.currentTarget.setAttribute('aria-pressed', String(on));
      } });
      const render = (msg) => this.setStep({
        eyebrow: 'Mic & playback',
        title: 'Hear how you sound',
        text: msg || 'Record 6 seconds and play it back. Wear headphones for "Hear myself".',
        actions: [
          { label: 'Record 6 seconds', primary: true, icon: 'record', onclick: () => { ga('headset_test_tool', { tool: 'mic', action: 'record' }); rec(); } },
          last ? { label: 'Play', onclick: () => { ga('headset_test_tool', { tool: 'mic', action: 'play' }); this.playRecording(last); } } : null,
          last ? { label: 'Download (.wav)', short: 'Save .wav', icon: 'download', onclick: () => { ga('headset_test_tool', { tool: 'mic', action: 'download' }); this.downloadWav(last, 'mic-test.wav'); } } : null,
          monitorBtn(),
        ],
      });
      const rec = async () => {
        this.setStep({ eyebrow: 'Mic & playback', title: 'Recording', text: 'Talk normally.', listen: true, countdown: 6 });
        const s = await this.record(6, (f) => this.setProgress(f));
        last = s.data;
        const st = speechStats(s.data, s.sr);
        const bw = st.speechDb > T.speechMissing ? analyseBandwidth(s.data, s.sr) : null;
        const bwText = bw ? ` Voice bandwidth: ${BANDWIDTH[bw.cls].label}, about ${(bw.cutoffHz / 1000).toFixed(1)} kHz.` : '';
        render(`Speech ${fmtDb(st.speechDb)}, peak ${fmtDb(st.peakDb)}, background ${fmtDb(st.noiseDb)}.${bwText}`);
      };
      render();
    }

    compareTool() {
      const slots = (this.state.compare = this.state.compare || { A: null, B: null });
      // Two tiles, A and B: what each take is, plus its own small record dot. The one
      // big button is always the next thing to do: record A, record B, then play.
      const tiles = (playing) => el('ul', { class: 'hsp-ht-slots' }, ['A', 'B'].map((k) => {
        const s = slots[k];
        return el('li', { class: `hsp-ht-slot${s ? ' is-done' : ''}${playing === k ? ' is-on' : ''}`, 'data-slot': k },
          el('span', { class: 'hsp-ht-slot-key', text: k }),
          el('span', { class: 'hsp-ht-slot-body' },
            el('strong', { text: s ? s.device : 'Not recorded yet' }),
            s ? el('span', { text: `${BANDWIDTH[s.bw.cls] ? BANDWIDTH[s.bw.cls].label : 'Voice not detected'}, background ${fmtDb(s.stats.noiseDb)}` }) : null),
          playing ? null : this.toolEl({ label: s ? `Record ${k} again` : `Record ${k}`, short: s ? 'Redo' : 'Record', icon: 'record', onclick: () => rec(k) }));
      }));
      const render = () => this.setStep({
        eyebrow: 'Compare two mics',
        title: 'Which mic sounds better?',
        text: 'Record A, switch the Microphone list above to the second mic, record B, then play them and switch between them.',
        extra: el('div', null, el('blockquote', { class: 'hsp-ht-sentence', text: SENTENCE }), tiles(null)),
        actions: [
          !slots.A ? { label: 'Record A', primary: true, icon: 'record', onclick: () => rec('A') }
            : !slots.B ? { label: 'Record B', primary: true, icon: 'record', onclick: () => rec('B') }
              : { label: 'Play and switch A/B', primary: true, icon: 'play', onclick: () => { ga('headset_test_tool', { tool: 'compare', action: 'play' }); ab(); } },
        ],
      });
      const rec = async (k) => {
        ga('headset_test_tool', { tool: 'compare', action: `record_${k.toLowerCase()}` });
        this.setStep({ eyebrow: 'Compare two mics', title: `Recording mic ${k}`, quote: SENTENCE, listen: true, countdown: 6 });
        const s = await this.record(6, (f) => this.setProgress(f));
        const stats = speechStats(s.data, s.sr);
        slots[k] = { data: s.data, stats, bw: stats.speechDb > T.speechMissing ? analyseBandwidth(s.data, s.sr) : { cls: 'unknown' }, device: this.device ? this.device.name : 'Microphone' };
        ga('headset_test_step', { step: 'compare', result: k });
        render();
      };
      const ab = () => {
        this.stopPlayback();
        // Match both to the quieter take, so matching only ever turns one down and
        // can never push a clip past full scale.
        const quiet = Math.min(slots.A.stats.speechDb, slots.B.stats.speechDb);
        const gainFor = (s) => (Number.isFinite(quiet) && Number.isFinite(s.stats.speechDb) ? Math.pow(10, (quiet - s.stats.speechDb) / 20) : 1);
        const a = this.playBuffer(slots.A.data, { loop: true, gain: gainFor(slots.A) });
        const b = this.playBuffer(slots.B.data, { loop: true, gain: 0 });
        const gA = gainFor(slots.A);
        const gB = gainFor(slots.B);
        this.playing = [a, b];
        const t = this.ctx.currentTime + 0.05;
        a.source.start(t);
        b.source.start(t);
        let onA = true;
        this.setStep({
          eyebrow: 'Compare two mics',
          title: 'Now playing: A',
          meter: false,
          extra: tiles('A'),
          actions: [
            { label: 'Switch A/B', primary: true, onclick: () => {
              onA = !onA;
              ga('headset_test_tool', { tool: 'compare', action: 'switch' });
              a.gain.gain.value = onA ? gA : 0;
              b.gain.gain.value = onA ? 0 : gB;
              this.ui.stepHead.querySelector('.hsp-ht-step-title').textContent = `Now playing: ${onA ? 'A' : 'B'}`;
              this.ui.stepBody.querySelectorAll('[data-slot]').forEach((n) => n.classList.toggle('is-on', n.dataset.slot === (onA ? 'A' : 'B')));
            } },
            { label: 'Stop', onclick: () => { this.stopPlayback(); render(); } },
          ],
        });
      };
      render();
    }

    speakerTool() {
      const sr = () => this.ctx.sampleRate;
      const noise = (seed) => pinkNoise(Math.round(sr() * 1.2), 0.2, seed);
      const go = (opts, seed) => {
        this.stopPlayback();
        const p = this.playBuffer(noise(seed), opts);
        this.playing = [p];
        p.source.start();
      };
      const sweepEx = el('p', { class: 'hsp-ht-hz', 'aria-live': 'off', text: '' });
      const sweep = () => {
        ga('headset_test_tool', { tool: 'speakers', action: 'sweep_start' });
        this.stopPlayback();
        const ctx = this.ctx;
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        const t0 = ctx.currentTime + 0.05;
        const dur = 20;
        g.gain.setValueAtTime(0, t0);
        g.gain.linearRampToValueAtTime(0.12, t0 + 0.1);
        o.frequency.setValueAtTime(20, t0);
        o.frequency.exponentialRampToValueAtTime(20000, t0 + dur);
        o.connect(g).connect(ctx.destination);
        o.start(t0);
        o.stop(t0 + dur);
        this.playing = [{ source: o }];
        const hz = () => Math.round(20 * Math.pow(1000, Math.min(1, Math.max(0, (ctx.currentTime - t0) / dur))));
        const tick = () => { sweepEx.textContent = `${hz().toLocaleString()} Hz`; this.sweepTimer = requestAnimationFrame(tick); };
        tick();
        this.setStep({
          eyebrow: 'Speakers',
          title: 'Frequency sweep',
          text: 'The tone rises from 20 Hz to 20,000 Hz. Press Stop when you can no longer hear it.',
          extra: sweepEx,
          actions: [{ label: 'Stop', primary: true, onclick: () => { const f = hz(); ga('headset_test_tool', { tool: 'speakers', action: 'sweep_stop', hz: Math.round(f / 500) * 500 }); this.stopPlayback(); render(`You stopped hearing it at about ${f.toLocaleString()} Hz.`); } }],
        });
      };
      // Tap an ear cup to hear that side; the cup lights while it plays. The phase and
      // sweep checks are for people who go looking, so they are small pills below.
      const render = (msg) => {
        const onSide = (side) => {
          ga('headset_test_tool', { tool: 'speakers', action: side });
          go({ pan: { left: -1, right: 1, both: 0 }[side] }, { left: 3, right: 5, both: 9 }[side]);
          pic.flash(side);
        };
        const pic = this.tool ? this.speakersEl(onSide) : this.headsetEl(onSide, 'play');
        this.setStep({
          eyebrow: this.tool ? 'Speaker test' : 'Speakers',
          title: this.tool ? 'Left and right test' : 'Check your speakers',
          text: msg || (this.tool
            ? 'Tap the left or right side to play a sound there, or Both for the middle. Keep your volume low to start.'
            : 'Tap an ear cup to play a sound on that side, or Both for the middle.'),
          meter: false,
          extra: el('div', null, pic,
            el('p', { class: 'hsp-ht-hint hsp-ht-more-tests', text: 'More tests: "In phase" should sound centered and "Out of phase" wide and hollow. If it is the other way round, one side is wired backwards.' })),
          actions: [
            { label: 'In phase', icon: 'speaker', onclick: () => { ga('headset_test_tool', { tool: 'speakers', action: 'phase_in' }); go({}, 13); } },
            { label: 'Out of phase', icon: 'speaker', onclick: () => { ga('headset_test_tool', { tool: 'speakers', action: 'phase_out' }); go({ invertRight: true }, 13); } },
            { label: 'Frequency sweep', short: 'Sweep', icon: 'waves', onclick: sweep },
          ],
        });
      };
      render();
    }

    echoTool() {
      const show = (echo) => {
        const row = { name: 'Echo', ...scoreEcho(echo) };
        this.setStep({
          eyebrow: 'Echo test',
          title: 'Result',
          meter: false,
          extra: el('ul', { class: 'hsp-ht-rows hsp-ht-rows--inline' }, this.rowEl(row)),
          actions: [{ label: 'Run again', primary: true, onclick: () => this.echoRun(show) }],
        });
      };
      this.echoRun(show);
    }

    /* ---------- mini (blog embed) ---------- */

    miniFlow() {
      const run = async () => {
        this.setStep({ eyebrow: 'Quick mic check', title: 'Stay quiet', text: 'Measuring background noise.', countdown: 2 });
        await wait(400);
        const q = await this.record(2, (f) => this.setProgress(f));
        const noiseDb = toDb(rms(q.data, Math.floor(q.data.length * 0.1)));
        this.setStep({ eyebrow: 'Quick mic check', title: 'Now read this out loud', quote: SENTENCE, listen: true, countdown: 5 });
        const s = await this.record(5, (f) => this.setProgress(f));
        const stats = speechStats(s.data, s.sr);
        const bw = stats.speechDb > T.speechMissing ? analyseBandwidth(s.data, s.sr) : null;
        const rows = [
          { name: 'Microphone', ...scoreDevice(this.device, this.defaultLabel) },
          { name: 'Level', ...scoreLevel(stats) },
          { name: 'Background noise', ...scoreNoise(noiseDb, stats.speechDb) },
          { name: 'Voice quality', ...scoreBandwidth(bw, this.device) },
        ].filter((x) => x.status !== 'skip');
        this.usage.completed = true;
        ga('headset_test_complete', { verdict: verdict(rows), bandwidth_class: bw ? bw.cls : 'unknown', connection: this.device ? this.device.connection : 'unknown', brand: (this.device && this.device.brand) || 'unknown', mode: 'mini' });
        this.setStep({
          eyebrow: 'Quick mic check',
          title: { pass: 'Your mic sounds good', warn: 'Your mic works, with something to fix', fail: 'Your mic needs attention' }[verdict(rows)],
          meter: false,
          extra: el('div', null,
            el('ul', { class: 'hsp-ht-rows hsp-ht-rows--inline' }, rows.map((x) => this.rowEl(x))),
            el('a', { class: 'hsp-ht-cta', href: this.cfg.fullUrl, 'data-ht-cta': 'full-test', text: 'Run the full headset test: speakers, echo and a report for IT' }),
            this.feedbackEl('test')),
          actions: [{ label: 'Play my recording', short: 'Play', onclick: () => this.playRecording(s.data) }, { label: 'Test again', short: 'Redo', icon: 'again', onclick: run }],
        });
      };
      this.setStep({ eyebrow: 'Quick mic check', title: 'Ready when you are', text: 'Takes 7 seconds: 2 quiet, 5 talking.', listen: true, actions: [{ label: 'Start the check', primary: true, onclick: run }] });
    }
  }

  if (!customElements.get('hsp-headset-test')) customElements.define('hsp-headset-test', HspHeadsetTest);
}

/** A few lines on making the headset the default, for this visitor's system. */
function defaultSteps(p) {
  const ua = navigator.userAgent;
  const mac = /Mac OS X/.test(ua) && !/iPhone|iPad/.test(ua);
  const win = /Windows/.test(ua);
  const out = [];
  if (win) {
    out.push('Right-click the speaker icon on the taskbar and choose Sound settings.');
    out.push('Under Output, choose your headset. Under Input, choose your headset.');
    if (p.comm || p.mic) out.push('For calls, open More sound settings, go to the Recording tab, right-click your headset and choose Set as Default Communication Device.');
  } else if (mac) {
    out.push('Open the Apple menu, then System Settings, then Sound.');
    out.push('Choose your headset on the Output tab and on the Input tab.');
  } else {
    out.push('Open your device\u2019s sound settings and choose your headset for both input and output, or disconnect the other audio devices.');
  }
  out.push('Reload this page and run the test again.');
  return out;
}

function safeJson(s) {
  try { return s ? JSON.parse(s) : {}; } catch (e) { return {}; }
}

if (typeof window !== 'undefined' && typeof customElements !== 'undefined') defineElement();
